/* ============================================================
 * src/screens/content/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-content.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. The MarkerEditor, Analyse,
// EditorialInbox, and AiSuggestions surfaces in this bundle bind to the
// real assets query where one exists, and to honest skeleton/empty
// states where no public surface is landed. No fixture dependency
// remains in this file.
import { fmtDur, fmtClock, ago, statusLabels } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { PosterGenerator } from '../pending/_player';
// MA-34 (CONTRACTS.md section 9.10): the asset page renders the headless
// `components/asset-player` player, which exposes the seek handle the deep
// link and the transcript tab go through and applies the origin correction
// of time rule 9. It is loaded lazily: the player (Vidstack + hls.js) is
// only fetched when an asset is actually playable, and the bundle's other
// screens — and their tests, which stub `../pending/_player` — never load it.
// The handle's type lives beside the component; the barrel does not
// re-export it (not an output of card MA-34).
const AssetPlayer = React.lazy(() =>
  import('../../components/asset-player').then((m) => ({ default: m.AssetPlayer })),
);
import type { AssetPlayerHandle } from '../../components/asset-player/AssetPlayer';
import { ReplaceFileModal, TrashModal } from '../pending/_bundle';
import { AudioVersionTab } from './_audio';
// NMW-1166 — per-surface decomposition: the "Transcripción" tab is its own
// file, not a wholesale decomposition of this bundle.
import { TranscriptTab } from './TranscriptTab';
import { FramesTab } from './FramesTab';
import { SceneList } from './SceneList';
import { LinkImportPanel } from './LinkImportPanel';
import { intakePhaseLabel } from '../../lib/intake/intakePhase';
// NMW-1170 — analysis-coverage surface (per-surface decomposition, same rule).
// The provider issues ONE batched `getCoverageForAssets` subscription for the
// visible page; the badge cells read it from context. No per-row query.
import {
  AssetCoverageBadge,
  AssetCoverageDetail,
  AssetCoverageProvider,
  FleetCoverageStrip,
  ModalityChips,
} from './AnalysisCoverage';
// MA-82 — "Opciones", "Reintentar" and "Cancelar" of the analysis panel are
// admins only: disabled with a tooltip for anyone else, never hidden.
import { useIsAdmin } from '../../hooks/useCurrentMember';
import { Provenance, type SourceKind } from '../../components/Provenance';
// T328 (FR-005) — FeedSources is the only export in this bundle that needs
// live Convex bindings. Creation is Payload-owned (see PRD §FR-005); the
// "Añadir fuente" affordance link-outs to Payload's admin form rather than
// invoking a Convex mutation that would inject a row outside the sync
// contract (`convex/sync/ingestSourcesReceiver.ts`).
import { useQuery, useAction, useMutation, usePaginatedQuery } from 'convex/react';
import { useBlocker } from 'react-router-dom';

// Guard route/prop ids before they reach a `v.id(...)` validator: a malformed
// id (mock fixture ids like `as_13` from the command palette, hand-typed URLs)
// would throw an ArgumentValidationError past the error boundary. Convex ids
// are long lowercase-alphanumeric strings; anything else → 'skip' the query
// and let the existing not-found/empty states render.
const isConvexId = (s?: string): boolean => !!s && /^[a-z0-9]{20,}$/.test(s);
import { api } from '@convex/api';
// The primitives-barrel `useMutationWithToast` (imported above) is the legacy
// in-memory `(fn, opts)` variant; the REAL Convex hook returns `{ run, loading }`.
import { useMutationWithToast as useConvexMutationWithToast } from '../../hooks/useMutationWithToast';
// NMW-932 (parent NMW-913) — the folder plumbing shipped by NMW-931. The
// Library consumes the shared pieces rather than growing its own: the
// breadcrumb trail, the move dialog (decision D4 — dialog, never DnD), and
// the persisted three-mode view switch.
import { FolderBreadcrumbs } from '../../components/FolderBreadcrumbs';
import { MoveToFolderDialog } from '../../components/MoveToFolderDialog';
import type { FolderNode } from '../../lib/folderTree';
// NMW-913 follow-up (owner, 2026-08-17): the grid and row renderers are GONE.
// The library is a file manager now — one dense table, folders as rows above
// files. There is no view switch left to persist, so `useViewMode` is unused
// here (and deleted outright).
import { TagEditor } from '../../components/tags/TagEditor';
import { TagAutocompleteInput, type TagEntry, type TagKind } from '../../components/tags/TagAutocompleteInput';
import { putWithProgress, isSupported, fileTitleStem, MAX_BYTES, httpBase as resolveHttpBase, uploadMultipart, shouldUseMultipart, listPendingMpuUploads, fileMatchesMpuKey, discardPendingMpuUpload, mpuKey, abortMultipart } from '../../lib/uploadClient';
import type { PendingMpuUpload } from '../../lib/uploadClient';
import { supportsFsAccess, saveFileHandle, loadFileHandle, deleteFileHandle } from '../../lib/fileHandleStore';
// T332 (FR-010) — content library wiring. The adapter layer maps backend
// literals onto the operator-facing display vocabulary the screens render.
import {
  ASSET_TYPE_LABELS,
  adaptAssetType,
  adaptTranscodingStatus,
  type AssetType,
} from '../../lib/adapters';

/* ============================================================
   T332 (FR-010) — Backend → screen row adapter for the public
   `api.assets.list` query. The prototype rendered a mock-shaped
   row (`id`, `type`, `status`, `markers`, `progress`, `addedAt`,
   `resolution` as a `'WIDTH×HEIGHT'` string); the live query
   surfaces the canonical backend shape (`_id`, `assetType`,
   `transcodingStatus`, `resolution: { width, height }`, etc.).
   This local function normalises one to the other so the existing
   JSX renders without invented fields (markers/progress are
   honestly absent — CLAUDE.md anti-pattern: "do not fabricate
   dashboard fields").
   ============================================================ */
interface AssetListRow {
  _id: string;
  title: string;
  assetType: AssetType;
  transcodingStatus: string;
  durationMs?: number;
  resolution?: { width: number; height: number };
  tags?: string[];
  thumbnailUrl?: string;
  playbackUrl?: string;
  createdAt?: number;
  transcodingProgress?: number;
  transcodingStartedAt?: number;
}
interface PrototypeAssetShape {
  id: string;
  title: string;
  /** Display label (Spanish) — `adaptAssetType` output. */
  type: string;
  /** Backend literal — used as the stable filter-button value. */
  assetType: AssetType;
  /** Mock-prototype 4-state vocabulary used by `assetStatusTone` /
   *  `statusLabels`: 'ready' | 'processing' | 'uploading' | 'failed'. */
  status: 'ready' | 'processing' | 'uploading' | 'failed';
  durationMs: number;
  resolution: string;
  tags: string[];
  thumbnailUrl?: string;
  playbackUrl?: string;
  addedAt: number;
  /** NMW-237: real transcode % (0-100) when emitted, else undefined. */
  transcodingProgress?: number;
  /** NMW-237: job start (Unix ms) — drives the elapsed-time fallback. */
  transcodingStartedAt?: number;
}

/**
 * Collapse the backend `transcodingStatus` (9 literals) to the
 * 4-string vocabulary the existing screen JSX understands
 * ('ready' / 'processing' / 'uploading' / 'failed'). `uploading`
 * round-trips by name; everything else routes through the
 * `adaptTranscodingStatus` 4-bucket adapter.
 */
function uiAssetStatus(backend: string): 'ready' | 'processing' | 'uploading' | 'failed' {
  if (backend === 'uploading') return 'uploading';
  const bucket = adaptTranscodingStatus(backend);
  if (bucket === 'completed') return 'ready';
  if (bucket === 'failure') return 'failed';
  // pending + in_progress both fall through to the "processing" UI affordance.
  return 'processing';
}

function adaptAssetRow(row: AssetListRow): PrototypeAssetShape {
  return {
    id: row._id,
    title: row.title,
    assetType: row.assetType,
    type: adaptAssetType(row.assetType),
    status: uiAssetStatus(row.transcodingStatus),
    durationMs: row.durationMs ?? 0,
    resolution: row.resolution
      ? `${row.resolution.width}×${row.resolution.height}`
      : '—',
    tags: row.tags ?? [],
    thumbnailUrl: row.thumbnailUrl,
    playbackUrl: row.playbackUrl,
    addedAt: row.createdAt ?? 0,
    transcodingProgress: row.transcodingProgress,
    transcodingStartedAt: row.transcodingStartedAt,
  };
}

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Content Library arc
   ============================================================ */

function assetStatusTone(s) { return s === 'ready' ? 'good' : s === 'processing' ? 'warn' : s === 'uploading' ? 'info' : 'neutral'; }

/**
 * T332 (FR-010, WIRE-4) — `/library` wired to `api.assets.list`.
 *
 * Honest-state pattern (per CLAUDE.md §3): `useQuery` returns `undefined`
 * during the in-flight subscription, an empty array when the table is
 * empty, and the row list when data lands. Each branch renders a
 * distinct affordance — no mock fallback, no fabricated counts.
 *
 * Filter design: the four type-bucket buttons store backend literals
 * (`vod` / `filler` / `house_ad` / `bumper`); display labels come from
 * `ASSET_TYPE_LABELS` so the operator-facing UI stays Spanish. The
 * 5th option is the sentinel `'all'` (no filter). The Select input
 * uses the same literal values for parity.
 */
const LIBRARY_TYPE_FILTERS: Array<'all' | AssetType> = [
  'all', 'vod', 'filler', 'house_ad', 'bumper',
];
const LIBRARY_TYPE_LABELS: Record<'all' | AssetType, string> = {
  all: 'Todos',
  ...ASSET_TYPE_LABELS,
};

// NMW-397 — tag facet labels (backend literal → Spanish label).
const TAG_KIND_LABELS: Record<string, string> = {
  channel: 'Canal',
  program: 'Programa',
  category: 'Categoría',
  freeform: 'Etiqueta',
};
/**
 * NMW-455 — text-only loading skeleton for the library grid.
 *
 * The shared `SkeletonCardGrid` primitive always reserves a media block
 * (`media` px tall) above the text lines, which is exactly the slot this
 * ticket removed from the real card. Rather than mutate the shared primitive
 * (it is used by other screens), the library ships its own skeleton whose
 * shape matches the text-only card: title line, meta line, chip row.
 */
function LibraryCardSkeletonGrid({ n = 12 }: { n?: number }) {
  return (
    <div className="circa-grid-6">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} style={{ border: '1px solid var(--border-cool)', borderRadius: 14, background: 'var(--canvas)', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Skeleton w="85%" h={13} r={4} />
          <div style={{ display: 'flex', gap: 8 }}><Skeleton w={54} h={18} r={999} /><Skeleton w={40} h={10} r={4} /></div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   NMW-932 — folder plumbing shared by the Library render below.
   ============================================================ */

/**
 * `foldersApi.listFolders` returns the DIRECT children of one parent, so a
 * flat "every folder" list (what `FolderBreadcrumbs` and `MoveToFolderDialog`
 * take) is assembled from the roots plus one probe per root. Depth is capped
 * at 2 by the backend (`convex/folders.ts`), so two levels is the whole tree.
 *
 * Honest-state: a result that is not an array (the subscription has not
 * landed) is `undefined`, never an empty array — "no folders yet" and "still
 * loading" must not render the same.
 */
/**
 * `convex/folders.ts` (NMW-928) landed after the last codegen run and
 * `convex/_generated/**` is frozen for this unit, so `api.folders` is not on
 * the generated type yet. The runtime `api` object is a path proxy that
 * resolves by NAME, so addressing the module through the untyped view is a
 * type hole only — the wire call is identical. Delete this shim the moment
 * `npx convex dev --once --typecheck enable` has run.
 */
const foldersApi: any = (api as any).folders;
// MA-82 — the media-analysis modules (CONTRACTS.md section 16.1), resolved the
// same way: the generated `api` type carries them only after a person runs
// codegen (rule 10); the runtime object resolves any path.
const mediaAnalysisApi: any = (api as any).mediaAnalysis;

const asFolderRows = (v: unknown): FolderNode[] | undefined =>
  Array.isArray(v) ? (v as FolderNode[]) : undefined;

/** Stable identity for a folder page, so the collector can skip no-op writes. */
const folderRowsSig = (rows: readonly FolderNode[] | undefined): string =>
  (rows ?? []).map((f) => `${f._id}:${f.name}`).join('|');

/**
 * One live subscription for one root folder's children, published upward.
 * Renders nothing — React's hook rules forbid a `useQuery` inside a loop, so
 * the loop becomes a list of these one-hook components.
 */
function FolderChildrenProbe({
  parentId,
  onRows,
}: {
  parentId: string;
  onRows: (parentId: string, rows: FolderNode[]) => void;
}) {
  const rows = asFolderRows(
    useQuery(foldersApi.listFolders, { domain: 'asset', parentId } as any),
  );
  useEffect(() => {
    if (rows) onRows(parentId, rows);
  }, [parentId, rows, onRows]);
  return null;
}

export function Library() {
  const [q, setQ] = useState('');
  const [type, setType] = useState<'all' | AssetType>('all');
  // NMW-932: three file-manager view modes (grid / row / column), persisted
  // per screen in localStorage by the shared hook. Replaces the old local
  // two-option `view` state; 'list' became 'row', 'column' is new.
  // NMW-913 follow-up: single view (the dense table). No mode state.
  // NMW-932 — folder navigation. The browsed folder is screen state (`null` =
  // the domain root); the route table maps `/library` with no folder segment,
  // so there is no cheap URL param to hang this on.
  const [folderId, setFolderId] = useState<string | null>(null);
  // NMW-932 — the ids the move dialog is open for (bulk selection, or one row
  // via the per-item action). `null` = the dialog is closed.
  const [moveIds, setMoveIds] = useState<string[] | null>(null);
  // NMW-932 — create / rename folder form state (`null` = closed).
  const [folderForm, setFolderForm] = useState<
    { mode: 'create' | 'rename'; id?: string; name: string } | null
  >(null);
  // NMW-932 — column view paginates the loaded rows client-side.
  const [colPage, setColPage] = useState(0);
  // NMW-413: multi-tag server-side filter. Selected facet keys are
  // `${kind}::${normalizedTag}`; the query intersects (tagMatchMode "all").
  const [selKeys, setSelKeys] = useState<Set<string>>(new Set());

  // NMW-411: bulk-tag selection. `selectMode` reveals per-card checkboxes;
  // `selected` is the set of chosen asset ids; `bulkKind` scopes the add/remove.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkKind, setBulkKind] = useState<TagKind>('freeform');
  const [bulkTag, setBulkTag] = useState<TagEntry | null>(null);

  // NMW-398 — debounce the search box so we hit Convex once the operator pauses
  // typing, not on every keystroke.
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  // NMW-413 (BLOCKER-2 / MINOR-6) — facet chips read the RESILIENT vocabulary
  // (`listTagVocabulary`): the registry catalog UNIONed with the live in-use
  // tags, so the bar works on pre-existing data even before the registry
  // backfill runs. `minUsage: 1` keeps zero-usage catalog entries OUT of the
  // filter bar (they could only ever yield the empty state); the full catalog
  // stays available to the editor autocomplete. Honest-state: `undefined`
  // while loading.
  const facetData = useQuery(api.assetQueries.listTagVocabulary, { minUsage: 1, limit: 200 }) as
    | { tags: Array<{ tag: string; kind: string; normalizedTag: string; usageCount: number }>; count: number }
    | undefined;
  const facets = facetData?.tags ?? [];
  const fkey = (f: { kind: string; normalizedTag: string }) => `${f.kind}::${f.normalizedTag}`;
  const selectedFacets = facets.filter((f) => selKeys.has(fkey(f)));
  // MAJOR-3: carry each selected facet's OWN kind through to the query so a
  // "Canal: sports" chip matches only channel-kind rows (not category:sports).
  const selTagFacets = selectedFacets.map((f) => ({ tag: f.normalizedTag, kind: f.kind }));
  const tagFilterActive = selTagFacets.length > 0;

  // NMW-411 — bulk tag mutation. One atomic call over the selected asset ids.
  const bulkApplyM = useConvexMutationWithToast(api.mutations.assets.bulkApplyAssetTags, {
    logLabel: 'library_bulkApplyAssetTags',
    mutationName: 'assets.bulkApplyAssetTags',
  });

  /* ---- NMW-932: folders --------------------------------------------------
     Root folders come from one subscription; each root's children come from a
     `FolderChildrenProbe` (rendered below) so the flat list handed to the
     breadcrumbs and the move dialog is the WHOLE tree, not just the branch the
     operator happens to be standing in. */
  const rootFolders = asFolderRows(
    useQuery(foldersApi.listFolders, { domain: 'asset' } as any),
  );
  const [childMap, setChildMap] = useState<Record<string, FolderNode[]>>({});
  const collectChildren = useCallback((parentId: string, rows: FolderNode[]) => {
    setChildMap((prev) => {
      if (folderRowsSig(prev[parentId]) === folderRowsSig(rows)) return prev;
      return { ...prev, [parentId]: rows };
    });
  }, []);
  const allFolders = useMemo(() => {
    const out: FolderNode[] = [...(rootFolders ?? [])];
    const seen = new Set(out.map((f) => f._id));
    for (const r of rootFolders ?? []) {
      for (const c of childMap[r._id] ?? []) {
        if (!seen.has(c._id)) { seen.add(c._id); out.push(c); }
      }
    }
    return out;
  }, [rootFolders, childMap]);
  const currentFolder = folderId ? allFolders.find((f) => f._id === folderId) : undefined;
  // An unresolved folder id (deleted in another tab) is treated as depth 2 —
  // fail-closed: no "nueva carpeta" affordance we cannot honour.
  const currentDepth = folderId ? (currentFolder?.depth ?? 2) : 0;
  const subfolders = folderId ? (childMap[folderId] ?? []) : (rootFolders ?? []);
  // Three-state scope arg (convex/lib/folderScope.ts): "root" = unfiled items.
  const folderScope = folderId ?? 'root';

  // NMW-932 — folder CRUD. `deleteFolder` rejects a non-empty folder
  // (decision D3); the wrapper surfaces that server error as a real error
  // toast — never a fabricated success.
  const createFolderM = useConvexMutationWithToast(foldersApi.createFolder, {
    logLabel: 'library_createFolder',
    mutationName: 'folders.createFolder',
    successMessage: 'Carpeta creada',
    errorMessagePrefix: 'No se pudo crear la carpeta',
  });
  const renameFolderM = useConvexMutationWithToast(foldersApi.renameFolder, {
    logLabel: 'library_renameFolder',
    mutationName: 'folders.renameFolder',
    successMessage: 'Carpeta renombrada',
    errorMessagePrefix: 'No se pudo renombrar la carpeta',
  });
  const deleteFolderM = useConvexMutationWithToast(foldersApi.deleteFolder, {
    logLabel: 'library_deleteFolder',
    mutationName: 'folders.deleteFolder',
    successMessage: 'Carpeta eliminada',
    errorMessagePrefix: 'No se pudo eliminar la carpeta',
  });

  const searching = debouncedQ.length > 0;
  // The operator has typed but the debounce hasn't fired yet — treat as loading
  // so the browse list doesn't flash under an about-to-change search box.
  const searchPending = q.trim().length > 0 && q.trim() !== debouncedQ;

  // BROWSE — cursor pagination, newest-first, walks past the old 100-row cap
  // (NMW-398). Skipped while a search term is active so we never hold two live
  // subscriptions. First use of `usePaginatedQuery` in this frontend.
  const browse = usePaginatedQuery(
    api.assets.listPaginated,
    // NMW-932: the browse page is scoped to the folder being viewed.
    searching || tagFilterActive ? 'skip' : ({ folderScope } as any),
    { initialNumItems: 50 },
  );

  // SEARCH — whole-table full-text over `search_title`; finds OLD content the
  // browse cap used to hide. Server-side, replacing the old client `.includes()`.
  const searchRaw = useQuery(
    api.assetQueries.searchAssetsByTitle,
    searching ? { searchTerm: debouncedQ, limit: 50 } : 'skip',
  ) as AssetListRow[] | undefined;

  // TAG FILTER — NMW-413 server-side multi-tag filter via
  // `api.assets.list({ tags, tagMatchMode: "all" })`. Active only when at
  // least one tag chip is selected and no search term is present. AND
  // semantics: an asset must carry EVERY selected tag.
  const tagRaw = useQuery(
    api.assets.list,
    !searching && tagFilterActive
      ? ({ tagFacets: selTagFacets, tagMatchMode: 'all' as const, folderScope } as any)
      : 'skip',
  ) as AssetListRow[] | undefined;

  // Honest header total, independent of how many pages are loaded. NMW-932:
  // scoped to the current folder so the number matches the list it labels.
  const totalCount = useQuery(api.assetQueries.countAssets, { folderScope } as any) as
    | number
    | undefined;

  // Loaded + adapted rows (type filter still applied client-side over the
  // loaded set — NMW-398 phase 1 — so the type-pill counts stay meaningful).
  const allRows = useMemo(() => {
    if (searching) {
      return searchRaw === undefined ? undefined : searchRaw.map(adaptAssetRow);
    }
    if (tagFilterActive) {
      return tagRaw === undefined ? undefined : tagRaw.map(adaptAssetRow);
    }
    if (browse.status === 'LoadingFirstPage') return undefined;
    return (browse.results as AssetListRow[]).map(adaptAssetRow);
  }, [searching, searchRaw, tagFilterActive, tagRaw, browse.results, browse.status]);

  const filtered = useMemo(() => {
    if (allRows === undefined) return undefined;
    return type === 'all' ? allRows : allRows.filter((a) => a.assetType === type);
  }, [allRows, type]);

  const canLoadMore = !searching && !tagFilterActive && browse.status === 'CanLoadMore';
  const loadingMore = !searching && !tagFilterActive && browse.status === 'LoadingMore';

  // NMW-411 — multi-select helpers over the currently-visible rows.
  const visibleIds = (filtered ?? []).map((a) => a.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleIds));
  const clearSelection = () => setSelected(new Set());
  // Apply/remove ONE typed tag across the selection, chunked to the server's
  // 1..100-per-call cap. Each chunk surfaces its own honest toast.
  const runBulkTag = async (op: 'add' | 'remove') => {
    if (!bulkTag || selected.size === 0) return;
    const ids = Array.from(selected);
    for (let i = 0; i < ids.length; i += 100) {
      await bulkApplyM.run({
        assetIds: ids.slice(i, i + 100) as any,
        tag: bulkTag.tag,
        kind: bulkTag.kind,
        op,
      });
    }
    clearSelection();
    setBulkTag(null);
  };

  /* ---- NMW-932: move + folder actions ------------------------------------
     The move verb REUSES the NMW-411 multi-select machinery above; the
     100-per-call chunking lives in `MoveToFolderDialog` (same cap, same shape
     as `runBulkTag`). */
  const openMove = (ids: string[]) => {
    if (ids.length > 0) setMoveIds(ids);
  };
  const goToFolder = (id: string | null) => {
    setFolderId(id);
    clearSelection();
    setColPage(0);
  };
  const submitFolderForm = async () => {
    if (!folderForm) return;
    const name = folderForm.name.trim();
    if (!name) return;
    try {
      if (folderForm.mode === 'create') {
        await createFolderM.run({
          domain: 'asset',
          name,
          ...(folderId ? { parentId: folderId } : {}),
        } as any);
      } else if (folderForm.id) {
        await renameFolderM.run({ folderId: folderForm.id, name } as any);
      }
      setFolderForm(null);
    } catch {
      // The wrapper already raised the real error toast (honest-state); the
      // form stays open with the typed name so the operator can fix it.
    }
  };
  const removeFolder = async (id: string) => {
    try {
      await deleteFolderM.run({ folderId: id } as any);
    } catch {
      // Decision D3: a non-empty folder is REJECTED server-side and the
      // wrapper toasts that error verbatim. Nothing is faked here.
    }
  };

  // Column pagination resets whenever the underlying result set changes.
  useEffect(() => {
    setColPage(0);
  }, [folderId, type, debouncedQ, tagFilterActive]);

  // Title/subtitle removed for a denser, content-first layout — the toolbar
  // buttons (Papelera / Importar / Subir) now sit beside the compact stats card.
  // Compact toolbar: the two secondary actions (Papelera / Importar) are
  // icon-only with tooltips so the whole toolbar fits BESIDE the stats card on
  // one row; the primary "Subir contenido" keeps its label.
  const toolbar = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* NMW-411 — toggle multi-select for bulk tagging. */}
      <Btn
        variant={selectMode ? 'solid' : 'ghost'}
        size="sm"
        icon="check"
        title="Seleccionar para etiquetar"
        onClick={() => {
          setSelectMode((on) => !on);
          if (selectMode) clearSelection();
        }}
      >
        {selectMode ? 'Salir' : 'Seleccionar'}
      </Btn>
      <Btn variant="ghost" size="sm" icon="trash" title="Papelera" onClick={() => navigate('/library/trash')} />
      <Dropdown trigger={<Btn variant="ghost" size="sm" icon="plus" iconRight="chevronD" title="Importar" />} items={[
        { label: 'Desde URL', icon: 'linkIcon', onClick: () => navigate('/library/upload#enlace') },
        { label: 'Desde una fuente', icon: 'database', onClick: () => navigate('/feed-sources') }]} />
      <AccessGate perm="asset_create"><Btn variant="solid" size="sm" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn></AccessGate>
    </div>
  );

  /* ---- NMW-932: folder bar (breadcrumbs + subfolder cells) ---------------
     Compact by owner constraint: a 24px breadcrumb line and 24px folder
     chips, no panel chrome around either. */
  const folderProbes = (rootFolders ?? []).map((f) => (
    <FolderChildrenProbe key={`probe-${f._id}`} parentId={f._id} onRows={collectChildren} />
  ));
  // NMW-913 follow-up: `folderCellBtn` / `folderIconBtn` deleted with the chip
  // strip they styled. Folder ROWS keep one icon-button style for their
  // rename / delete actions.
  const folderRowIconBtn: React.CSSProperties = {
    border: 'none', background: 'none', padding: 0, margin: 0, lineHeight: 0,
    cursor: 'pointer', color: 'var(--muted-slate)', display: 'inline-flex',
  };
  const folderBar = (
    <div data-testid="library-folder-bar" style={{ marginBottom: 14 }}>
      {folderProbes}
      <FolderBreadcrumbs
        folders={allFolders}
        folderId={folderId}
        rootLabel="Biblioteca"
        onNavigate={goToFolder}
        right={
          currentDepth < 2 ? (
            <Btn
              variant="ghost"
              size="sm"
              icon="plus"
              title="Nueva carpeta"
              onClick={() => setFolderForm({ mode: 'create', name: '' })}
            >
              Carpeta
            </Btn>
          ) : null
        }
      />
      {/* NMW-913 follow-up: the subfolder CHIP STRIP is gone — folders are
          rows in the main table now (see the folder row in the tbody). */}
    </div>
  );

  const folderModal = folderForm && (
    <Modal
      open
      onClose={() => setFolderForm(null)}
      width={380}
      title={<h2 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>{folderForm.mode === 'create' ? 'Nueva carpeta' : 'Renombrar carpeta'}</h2>}
      footer={
        <>
          <Btn size="sm" variant="ghost" onClick={() => setFolderForm(null)}>Cancelar</Btn>
          <Btn
            size="sm"
            variant="primary"
            disabled={!folderForm.name.trim() || createFolderM.loading || renameFolderM.loading}
            onClick={() => { void submitFolderForm(); }}
          >
            Guardar
          </Btn>
        </>
      }
    >
      <Input
        value={folderForm.name}
        onChange={(v: string) => setFolderForm((prev) => (prev ? { ...prev, name: v } : prev))}
        placeholder="Nombre de la carpeta"
      />
    </Modal>
  );

  const moveDialog = moveIds && (
    <MoveToFolderDialog
      open
      onClose={() => setMoveIds(null)}
      domain="asset"
      itemIds={moveIds}
      currentFolderId={folderId}
      folders={allFolders}
      onMoved={() => { clearSelection(); setMoveIds(null); }}
    />
  );

  // ---- (1) INITIAL BROWSE LOAD --------------------------------------------
  // Only the very first browse page (nothing typed) shows the full-page
  // skeleton. Once the search box is on screen we never unmount it — search
  // loading renders inline below so the input keeps focus (NMW-398).
  if (!searching && allRows === undefined) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>{toolbar}</div>
        {/* NMW-932: the folder bar survives the loading branch so a drill-out
            is always available, even while the folder's page is in flight. */}
        {folderBar}
        <SkeletonStatRow n={1} />
        <div style={{ height: 52 }} />
        <LibraryCardSkeletonGrid n={12} />
        {folderModal}
      </div>
    );
  }

  // ---- (2) EMPTY LIBRARY (no assets at all, not searching) -----------------
  // NMW-932: only at the ROOT. Inside a folder an empty result is "this folder
  // is empty", not "the library is empty" — and this branch drops the folder
  // bar, which would strand the operator with no way back out.
  if (!searching && folderId === null && allRows !== undefined && allRows.length === 0 && (totalCount ?? 0) === 0 && (rootFolders ?? []).length === 0) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>{toolbar}</div>
        {folderProbes}
        <Panel>
          <EmptyState
            icon="film"
            title="Sin contenido todavía"
            body="Sube tu primer video o conecta una fuente para llenar la biblioteca."
            action={
              <AccessGate perm="asset_create">
                <Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn>
              </AccessGate>
            }
          />
        </Panel>
      </div>
    );
  }

  // ---- (3) DATA ------------------------------------------------------------
  // Stats over the currently-loaded rows; "Videos" uses the server-side count
  // cache so it reflects the WHOLE library, not just loaded pages (NMW-398).
  const loadedRows = allRows ?? [];
  const readyCount = loadedRows.filter((a) => a.status === 'ready').length;
  const inFlightCount = loadedRows.filter((a) => a.status === 'processing' || a.status === 'uploading').length;
  const resultsLoading = filtered === undefined;

  // Compact, single-card stats — Videos / Listos / En proceso condensed into one
  // dense inline strip (replaces the old StatCard grid + the removed "Duración
  // total"), sitting on the same row as the toolbar buttons.
  const statDivider = <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--lightest-gray)' }} />;
  const statCell = (value: React.ReactNode, label: string, color?: string) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400, letterSpacing: '-0.4px', color: color ?? 'var(--ink)' }}>{value}</span>
      <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{label}</span>
    </div>
  );

  /* ---- NMW-932: filter + bulk toolbars, hoisted so the COLUMN view can
     render them inside one sticky wrapper (the toolbar must not scroll away)
     while grid / row keep the pre-existing layout untouched. ---- */
  const filtersPanel = (
    <Panel pad={16} style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ width: 300 }}><Input value={q} onChange={setQ} icon="search" placeholder="Buscar por título" /></div>
        <div style={{ width: 200 }}>
          <Select
            value={type}
            onChange={(v) => setType(v as 'all' | AssetType)}
            full
            options={LIBRARY_TYPE_FILTERS.map((v) => ({ value: v, label: LIBRARY_TYPE_LABELS[v] }))}
          />
        </div>
        <div style={{ flex: 1 }} />
        {/* NMW-913 follow-up: the grid/row/column switch is removed. One view. */}
      </div>
    </Panel>
  );

  // NMW-932: the bulk bar is now also reachable from the COLUMN view's always-on
  // checkbox column, so it shows whenever something is selected — not only while
  // the explicit `selectMode` toggle is on.
  const bulkPanel = (selectMode || selected.size > 0) && (
    <Panel pad={16} style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ width: 16, height: 16, cursor: 'pointer' }} />
          <span style={{ fontWeight: 500 }}>{selected.size > 0 ? `${selected.size} seleccionado(s)` : 'Seleccionar todo'}</span>
        </label>
        <div style={{ minWidth: 320, flex: 1 }}>
          <TagAutocompleteInput
            kind={bulkKind}
            onKindChange={setBulkKind}
            onPick={(e) => setBulkTag(e)}
            placeholder="Etiqueta a aplicar…"
          />
        </div>
        {bulkTag && (
          <CircaTag onRemove={() => setBulkTag(null)}>
            {bulkTag.kind === 'freeform' ? bulkTag.tag : `${TAG_KIND_LABELS[bulkTag.kind]}: ${bulkTag.tag}`}
          </CircaTag>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Btn variant="solid" size="sm" icon="plus" disabled={!bulkTag || selected.size === 0 || bulkApplyM.loading} onClick={() => { void runBulkTag('add'); }}>Añadir</Btn>
          <Btn variant="ghost" size="sm" icon="x" disabled={!bulkTag || selected.size === 0 || bulkApplyM.loading} onClick={() => { void runBulkTag('remove'); }}>Quitar</Btn>
          {/* NMW-932 — bulk move. Same selection, same 100-per-call chunking. */}
          <Btn variant="ghost" size="sm" icon="folder" disabled={selected.size === 0} onClick={() => openMove(Array.from(selected))}>Mover a…</Btn>
          <Btn variant="ghost" size="sm" disabled={selected.size === 0} onClick={clearSelection}>Limpiar</Btn>
        </div>
      </div>
    </Panel>
  );

  /* ---- NMW-932: COLUMN view — a dense table over the loaded rows, paginated
     at the bottom. No virtualization and no new dependency: pagination bounds
     the DOM at 25 rows, which is what virtualization would have bought. ---- */
  const COL_PAGE_SIZE = 25;
  /* NMW-913 follow-up (owner, 2026-08-17): folders are ROWS in this table now,
     sorted above the files, so the listing reads like a file manager instead of
     a chip strip plus a separate grid. Folders are hidden while SEARCHING —
     `searchAssetsByTitle` spans the whole library (see the scope note above),
     so folder rows next to cross-folder hits would imply a containment that
     isn't there. Both kinds share one pagination run. */
  type ListRow =
    | { kind: 'folder'; folder: FolderNode }
    | { kind: 'asset'; asset: PrototypeAssetShape };
  const colRows: ListRow[] = [
    ...(searching ? [] : subfolders.map((f): ListRow => ({ kind: 'folder', folder: f }))),
    ...(filtered ?? []).map((a): ListRow => ({ kind: 'asset', asset: a })),
  ];
  const colPageCount = Math.max(1, Math.ceil(colRows.length / COL_PAGE_SIZE));
  const colSafePage = Math.min(Math.max(0, colPage), colPageCount - 1);
  const colSlice = colRows.slice(colSafePage * COL_PAGE_SIZE, colSafePage * COL_PAGE_SIZE + COL_PAGE_SIZE);
  /* NMW-1170 — the ids whose analysis coverage the table needs. Exactly the
     rows ON SCREEN (<= COL_PAGE_SIZE = 25), so the batch query stays far under
     the backend's 200-id ceiling and the page never opens one subscription per
     row. Folder rows carry no coverage and are excluded. */
  const coverageIds = colSlice
    .filter((r): r is { kind: 'asset'; asset: PrototypeAssetShape } => r.kind === 'asset')
    .map((r) => r.asset.id);
  const TH: React.CSSProperties = { padding: '6px 10px', fontWeight: 500, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' };
  const TD: React.CSSProperties = { padding: '5px 10px', verticalAlign: 'middle', whiteSpace: 'nowrap' };
  // Shared transcode chip — the same honest rule as grid/row: a REAL percent
  // when the worker emitted one, else the elapsed time. Never a fabricated value.
  const progressChip = (a: PrototypeAssetShape) =>
    (a.status === 'processing' || a.status === 'uploading') &&
    (pct(a.transcodingProgress) !== undefined || a.transcodingStartedAt) ? (
      <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>
        {pct(a.transcodingProgress) !== undefined ? `${pct(a.transcodingProgress)}%` : ago(a.transcodingStartedAt!)}
      </span>
    ) : null;
  const moveOneBtn = (a: PrototypeAssetShape) => (
    <span onClick={(e) => { e.stopPropagation(); openMove([a.id]); }} style={{ display: 'inline-flex' }}>
      <Btn variant="ghost" size="sm" icon="folder" title="Mover a…" />
    </span>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <Panel pad={0} style={{ flex: '0 0 auto', width: 'fit-content', maxWidth: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '10px 18px' }}>
            {statCell(totalCount ?? loadedRows.length, 'Videos')}
            {statDivider}
            {statCell(readyCount, 'Listos', 'var(--good)')}
            {statDivider}
            {statCell(inFlightCount, 'En proceso', 'var(--warn)')}
          </div>
        </Panel>
        {toolbar}
      </div>
      {folderBar}
      {/* NMW-1170 — fleet coverage. Real counts from `getFleetCoverage`, with
          the window it counted over stated on the same line: the query counts
          exactly but over a bounded newest-first slice, so presenting the
          number as a library total would be a fabricated result. */}
      <FleetCoverageStrip />
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {LIBRARY_TYPE_FILTERS.map((tp) => {
          const n = tp === 'all' ? loadedRows.length : loadedRows.filter((a) => a.assetType === tp).length;
          return <button key={tp} onClick={() => setType(tp)} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontFamily: 'var(--font-sans)', display: 'inline-flex', alignItems: 'center', gap: 7,
            background: type === tp ? 'var(--ink-muted)' : 'transparent', color: type === tp ? '#fff' : 'var(--slate)', borderColor: type === tp ? 'var(--ink-muted)' : 'var(--border-cool)' }}>{LIBRARY_TYPE_LABELS[tp]}<span style={{ fontSize: 11, opacity: 0.7 }}>{n}</span></button>;
        })}
      </div>
      {/* NMW-413: multi-tag facet filter. Chips read the canonical tagRegistry
          catalog and toggle independently; the server intersects (AND) across
          every selected tag via `api.assets.list({ tags, tagMatchMode:"all" })`.
          "Todas" clears the whole set. */}
      {facets.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginRight: 4 }}>Etiquetas</span>
          <button onClick={() => setSelKeys(new Set())} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontFamily: 'var(--font-sans)',
            background: !tagFilterActive ? 'var(--ink-muted)' : 'transparent', color: !tagFilterActive ? '#fff' : 'var(--slate)', borderColor: !tagFilterActive ? 'var(--ink-muted)' : 'var(--border-cool)' }}>Todas</button>
          {facets.map((f) => {
            const k = fkey(f);
            const active = selKeys.has(k);
            return (
              <button key={k}
                onClick={() => setSelKeys((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; })}
                style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontFamily: 'var(--font-sans)', display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: active ? 'var(--ink-muted)' : 'transparent', color: active ? '#fff' : 'var(--slate)', borderColor: active ? 'var(--ink-muted)' : 'var(--border-cool)' }}>
                {f.kind !== 'freeform' && <span style={{ fontSize: 10, opacity: 0.65 }}>{TAG_KIND_LABELS[f.kind] ?? f.kind}:</span>}
                {f.tag}
                <span style={{ fontSize: 10, opacity: 0.6 }}>{f.usageCount}</span>
              </button>
            );
          })}
          {tagFilterActive && (
            <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>
              {selTagFacets.length} activa(s) · deben coincidir todas
            </span>
          )}
        </div>
      )}
      {/* NMW-411 bulk tag toolbar + NMW-932 "Mover a…". In COLUMN mode both
          toolbars ride inside a sticky wrapper so they never scroll away from
          a long table; grid / row keep the original in-flow placement. */}
      <div
        data-testid="library-toolbar"
        data-sticky=""
        style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--snow)', paddingTop: 4 }}
      >
        {filtersPanel}
        {bulkPanel}
      </div>
      {/* NMW-932 — honest scope note: title search is whole-library
          (`searchAssetsByTitle` takes no folder scope), so say so rather than
          letting the breadcrumb imply the results are folder-scoped. */}
      {searching && folderId !== null && (
        <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 12 }}>
          La búsqueda abarca toda la biblioteca, no solo esta carpeta.
        </div>
      )}
      {/* ---- RESULTS REGION: search loading | empty | grid/row/column ---- */}
      {resultsLoading || searchPending ? (
        <LibraryCardSkeletonGrid n={12} />
      ) : colRows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={searching ? 'search' : 'film'}
            title={searching ? 'Sin resultados' : 'Nada que mostrar'}
            body={searching
              ? `No se encontraron videos que coincidan con "${debouncedQ}".`
              : 'Ajusta el filtro por tipo o etiqueta para ver contenido.'}
          />
        </Panel>
      ) : (
        /* ---- NMW-932 COLUMN VIEW — dense table + bottom pagination ---- */
        /* NMW-1170: the provider wraps the table so ONE batched coverage
           subscription serves every `<AssetCoverageBadge>` cell below. */
        <AssetCoverageProvider assetIds={coverageIds}>
        <Panel pad={0}>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="library-column-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'var(--muted-slate)', textAlign: 'left', borderBottom: '1px solid var(--lightest-gray)' }}>
                  <th style={{ ...TH, width: 30 }}>
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todo"
                      checked={allSelected}
                      onChange={toggleAll}
                      style={{ width: 14, height: 14, cursor: 'pointer' }}
                    />
                  </th>
                  {/* Título carries the widest content (folder + file names);
                      give it the slack so the ellipsis is a rare event. */}
                  <th style={{ ...TH, width: '38%' }}>Título</th>
                  <th style={TH}>Tipo</th>
                  <th style={TH}>Resolución</th>
                  <th style={TH}>Duración</th>
                  {/* NMW-913 follow-up: the tag pills lived only in the deleted
                      GRID renderer. Losing the grid must not lose tag
                      visibility (NMW-397/411/412), so they get a column. */}
                  <th style={TH}>Etiquetas</th>
                  <th style={TH}>Estado</th>
                  {/* NMW-1170 — analysis coverage. "Estado" is the TRANSCODE
                      state; these are different pipelines and a converted video
                      can still be entirely un-analysed, so it gets its own
                      column rather than being folded into that pill. */}
                  <th style={TH}>Análisis</th>
                  <th style={{ ...TH, width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {colSlice.map((r) => r.kind === 'folder' ? (
                  /* NMW-913 follow-up — FOLDER ROW. Sorted above the files by
                     construction of `colRows`. No checkbox: the bulk toolbar
                     drives `moveAssetsToFolder`, which takes assets, not
                     folders — a folder is relocated with `moveFolder` from its
                     own row action, so offering a checkbox here would imply a
                     bulk operation that does not exist.
                     "Elementos" is deliberately EMPTY: a per-folder count would
                     need one countAssets subscription per row, and countAssets
                     currently full-scans the table at non-"all" scope. A real
                     count lands when that is fixed; an invented one never. */
                  <tr
                    key={`folder-${r.folder._id}`}
                    data-testid="library-folder-row"
                    data-folder-id={r.folder._id}
                    onClick={() => goToFolder(r.folder._id)}
                    style={{ cursor: 'pointer', borderBottom: '1px solid var(--lightest-gray)' }}
                  >
                    <td style={TD} />
                    <td style={{ ...TD, maxWidth: 0, overflow: 'hidden', fontWeight: 500 }} title={r.folder.name}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <Icon name="folder" size={12} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.folder.name}</span>
                      </span>
                    </td>
                    <td style={TD}>Carpeta</td>
                    <td className="t-mono" style={TD}>—</td>
                    <td className="t-mono" style={TD}>—</td>
                    <td style={TD} />
                    <td style={TD} />
                    {/* NMW-1170: a folder has no analysis coverage. Left blank
                        rather than badged — "sin analizar" on a folder would be
                        a category error. */}
                    <td style={TD} />
                    {/* Plain <button>s, not <Btn>: the Btn primitive
                        destructures a fixed prop list and DROPS data-testid,
                        which these rows are addressed by. */}
                    <td style={TD} onClick={(e) => e.stopPropagation()}>
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <button
                          type="button"
                          data-testid="folder-rename"
                          title="Renombrar"
                          style={folderRowIconBtn}
                          onClick={() => setFolderForm({ mode: 'rename', id: r.folder._id, name: r.folder.name })}
                        >
                          <Icon name="edit" size={11} />
                        </button>
                        <button
                          type="button"
                          data-testid="folder-delete"
                          title="Eliminar (debe estar vacía)"
                          style={folderRowIconBtn}
                          onClick={() => { void removeFolder(r.folder._id); }}
                        >
                          <Icon name="trash" size={11} />
                        </button>
                      </span>
                    </td>
                  </tr>
                ) : (() => { const a = r.asset; return (
                  <tr
                    key={a.id}
                    data-testid="library-column-row"
                    onClick={() => (selectMode ? toggleRow(a.id) : navigate('/library/' + a.id))}
                    style={{ cursor: 'pointer', borderBottom: '1px solid var(--lightest-gray)', background: selected.has(a.id) ? 'var(--lightest-gray)' : undefined }}
                  >
                    <td style={TD} onClick={(e) => { e.stopPropagation(); toggleRow(a.id); }}>
                      <input
                        type="checkbox"
                        aria-label={`Seleccionar ${a.title}`}
                        checked={selected.has(a.id)}
                        readOnly
                        style={{ width: 14, height: 14, cursor: 'pointer' }}
                      />
                    </td>
                    {/* NMW-913 follow-up: a `film` glyph marks a FILE, matching
                        the `folder` glyph on folder rows above — the row kind is
                        readable at a glance, not inferred from the Tipo column. */}
                    <td style={{ ...TD, maxWidth: 0, overflow: 'hidden', fontWeight: 500 }} title={a.title}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <Icon name="film" size={12} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                      </span>
                    </td>
                    <td style={TD}>{a.type}</td>
                    <td className="t-mono" style={TD}>{a.resolution}</td>
                    <td className="t-mono" style={TD}>{fmtDur(a.durationMs)}</td>
                    <td style={TD}>
                      {a.tags && a.tags.length > 0 ? (
                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          {a.tags.slice(0, 2).map((t) => (
                            <Pill key={t} tone="neutral" size="sm">{t}</Pill>
                          ))}
                          {a.tags.length > 2 && (
                            <span style={{ fontSize: 10, color: 'var(--muted-slate)' }}>+{a.tags.length - 2}</span>
                          )}
                        </span>
                      ) : null}
                    </td>
                    <td style={TD}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Pill tone={assetStatusTone(a.status)} size="sm">{statusLabels[a.status]}</Pill>
                        {progressChip(a)}
                      </span>
                    </td>
                    {/* NMW-1170 — reads the ONE batched coverage subscription
                        held by <AssetCoverageProvider> above; no query here. */}
                    <td style={TD}><AssetCoverageBadge assetId={a.id} /></td>
                    <td style={TD} onClick={(e) => e.stopPropagation()}>{moveOneBtn(a)}</td>
                  </tr>
                ); })())}
              </tbody>
            </table>
          </div>
          <div
            data-testid="library-column-pagination"
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderTop: '1px solid var(--lightest-gray)' }}
          >
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>
              {colRows.length} elemento(s) · página {colSafePage + 1} de {colPageCount}
            </span>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <Btn variant="ghost" size="sm" icon="chevronL" title="Anterior" disabled={colSafePage === 0} onClick={() => setColPage(colSafePage - 1)} />
              <Btn variant="ghost" size="sm" icon="chevronR" title="Siguiente" disabled={colSafePage >= colPageCount - 1} onClick={() => setColPage(colSafePage + 1)} />
            </span>
          </div>
        </Panel>
        </AssetCoverageProvider>
      )}

      {/* ---- LOAD MORE (browse only — NMW-398 pagination past 100) ---- */}
      {!searching && !resultsLoading && (canLoadMore || loadingMore) && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Btn
            variant="ghost"
            icon={loadingMore ? 'loader' : 'chevronD'}
            disabled={loadingMore}
            onClick={() => browse.loadMore(50)}
          >
            {loadingMore ? 'Cargando…' : 'Cargar más'}
          </Btn>
        </div>
      )}

      {/* NMW-932 — folder create/rename form + the shared move dialog. */}
      {folderModal}
      {moveDialog}
    </div>
  );
}

/* Upload — ports the v2 S3 + AWS Batch pipeline verbatim
 * (frontend-v2/src/components/library/UploadAssetPage.tsx). Three HTTP
 * round-trips against the Convex `.convex.site` host:
 *
 *   1. POST /api/upload/url        — creates the asset row in `uploading`
 *                                    and mints an S3 presigned PUT URL.
 *   2. PUT  <s3-presigned-url>     — file bytes go directly to S3
 *                                    (Content-Type must match step 1).
 *   3. POST /api/upload/complete   — flips the asset to `pending`, kicks
 *                                    the AWS Batch ingest workflow.
 *
 * NOT Cloudflare Stream — that's a wrong pipeline this repo has rejected
 * twice. Cloudflare Stream wires were stub code from a prototype lift. */
type UploadStatus = 'queued' | 'creating' | 'uploading' | 'completing' | 'done' | 'error';
type AssetType =
  | 'vod' | 'filler' | 'house_ad' | 'bumper' | 'slate' | 'promo'
  | 'movie' | 'shortFormVideo' | 'tvSpecial' | 'series';

interface UploadRow {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  errorMessage?: string;
  assetId?: string;
  cloudfrontUrl?: string;
}

const ASSET_TYPE_OPTIONS: ReadonlyArray<readonly [AssetType, string]> = [
  ['vod', 'VOD'], ['movie', 'Película'], ['tvSpecial', 'Especial'],
  ['series', 'Serie'], ['shortFormVideo', 'Video corto'], ['promo', 'Promo'],
  ['bumper', 'Bumper'], ['house_ad', 'House ad'], ['filler', 'Filler'],
  ['slate', 'Slate'],
];
const STATUS_LABEL: Record<UploadStatus, string> = {
  queued: 'En cola', creating: 'Registrando', uploading: 'Subiendo',
  completing: 'Finalizando', done: 'Listo', error: 'Error',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export function Upload() {
  const [drag, setDrag] = useState(false);
  const [queue, setQueue] = useState<UploadRow[]>([]);
  const [defaultAssetType, setDefaultAssetType] = useState<AssetType>('vod');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Live XHRs keyed by queue-row id, so the navigation guard and unmount
  // cleanup can `.abort()` an in-flight PUT.
  const xhrsRef = useRef<Map<string, XMLHttpRequest>>(new Map());
  // Row ids the operator explicitly cancelled (per-row Cancelar button). Lets
  // uploadOne's catch distinguish a hard cancel (tear down, no resume) from a
  // resumable pause (navigation/network).
  const canceledRef = useRef<Set<string>>(new Set());
  // ConfirmModal fires onConfirm() THEN onClose() on the confirm path; this
  // flag lets onClose skip resetting a blocker we just proceeded through.
  const proceedingRef = useRef(false);
  // Best-effort backend report: flips the asset row out of "uploading" so it is
  // never stuck. No success toast — this is bookkeeping, not an operator action.
  const markUploadFailed = useMutation(api.mutations.assets.markUploadFailed);

  // HTTP routes are mounted on the .convex.site host, not the WebSocket
  // .convex.cloud host — matches v2's UploadAssetPage URL swap.
  const httpBase = useMemo(() => resolveHttpBase(), []);

  const patch = (id: string, p: Partial<UploadRow>) =>
    setQueue(prev => prev.map(r => r.id === id ? { ...r, ...p } : r));

  // Tell the backend a created-but-never-completed upload failed. Swallows its
  // own errors — the UI row is already marked, and a failed report must never
  // surface a second toast or block teardown.
  const reportFailed = useCallback(
    async (assetId: string | undefined, reason: string) => {
      if (!assetId) return;
      try {
        await markUploadFailed({ assetId: assetId as any, reason });
      } catch {
        /* best-effort; the cron sweep is the backstop */
      }
    },
    [markUploadFailed],
  );

  const uploadOne = useCallback(async (row: UploadRow, assetType: AssetType) => {
    // Hoisted so the catch can report the (possibly partially-created) asset.
    let assetId: string | undefined;
    try {
      patch(row.id, { status: 'creating' });
      const contentType = row.file.type || 'application/octet-stream';

      // Large files (≥ MULTIPART_THRESHOLD_BYTES) take the chunked, parallel,
      // resumable multipart path (NMW-371/372). It creates the asset, uploads
      // the parts with real aggregate progress, and kicks the ingest workflow
      // internally. Small files fall through to the untouched single-PUT flow.
      if (shouldUseMultipart(row.file)) {
        // Bridge the multipart abort into the existing xhrsRef cancel path: a
        // shim exposing .abort() lets cancelInFlight() tear this upload down.
        const controller = new AbortController();
        xhrsRef.current.set(row.id, { abort: () => controller.abort() } as unknown as XMLHttpRequest);
        patch(row.id, { status: 'uploading' });
        const res = await uploadMultipart(row.file, {
          assetType,
          title: fileTitleStem(row.file.name),
          signal: controller.signal,
          onProgress: (f) => { patch(row.id, { progress: Math.round(f * 100) }); },
          onAssetId: (id, cf) => { assetId = id; patch(row.id, { assetId: id, cloudfrontUrl: cf }); },
        });
        xhrsRef.current.delete(row.id);
        patch(row.id, { assetId: res.assetId, cloudfrontUrl: res.cloudfrontUrl, status: 'done', progress: 100 });
        return;
      }

      // Step 1 — request presigned URL + create asset row.
      const stepOne = await fetch(`${httpBase}/api/upload/url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename:    row.file.name,
          contentType,
          title:       fileTitleStem(row.file.name),
          assetType,
        }),
      });
      if (!stepOne.ok) {
        throw new Error(`Registro rechazado (HTTP ${stepOne.status}): ${await stepOne.text()}`);
      }
      const { assetId: newAssetId, uploadUrl, cloudfrontUrl } = (await stepOne.json()) as {
        assetId: string;
        uploadUrl: string;
        cloudfrontUrl: string;
        s3Key: string;
      };
      assetId = newAssetId;
      patch(row.id, { assetId, cloudfrontUrl, status: 'uploading' });

      // Step 2 — PUT bytes to S3 with progress. Register the XHR so the
      // navigation guard / unmount can abort it; unregister when it settles.
      await putWithProgress(
        uploadUrl,
        row.file,
        contentType,
        (pct) => { patch(row.id, { progress: pct }); },
        (xhr) => { xhrsRef.current.set(row.id, xhr); },
      );
      xhrsRef.current.delete(row.id);

      // Step 3 — signal completion → flips asset to `pending` + kicks
      // AWS Batch ingest workflow.
      patch(row.id, { status: 'completing' });
      const stepThree = await fetch(`${httpBase}/api/upload/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, cloudfrontUrl }),
      });
      if (!stepThree.ok) {
        throw new Error(`Finalización rechazada (HTTP ${stepThree.status}): ${await stepThree.text()}`);
      }

      patch(row.id, { status: 'done', progress: 100 });
    } catch (err) {
      xhrsRef.current.delete(row.id);
      const msg = err instanceof Error ? err.message : String(err);
      // Explicit per-row cancel: cancelRow already tore down the S3 upload +
      // resume session + handle and reported the asset failed. Just settle the
      // row; do NOT surface it as resumable.
      if (canceledRef.current.has(row.id)) {
        canceledRef.current.delete(row.id);
        patch(row.id, { status: 'error', errorMessage: 'Subida cancelada.' });
        return;
      }
      // A resumable multipart interruption (network/retry exhaustion, not an
      // explicit cancel) keeps its localStorage session so re-adding the same
      // file resumes the already-uploaded parts. We must NOT mark the asset
      // failed in that case — /mpu/complete would then reject on resume. The
      // asset stays "uploading"; the 90-min stall sweep is the backstop.
      const resumable = !!(err && typeof err === 'object' && (err as { resumable?: boolean }).resumable);
      patch(row.id, {
        status: 'error',
        errorMessage: resumable ? `${msg} — reintenta el mismo archivo para reanudar` : msg,
      });
      if (!resumable) {
        // Don't leave the Convex row stuck at "uploading" — report the failure.
        // No-op on the backend if a real completion already raced ahead.
        void reportFailed(assetId, msg);
      }
    }
  }, [httpBase, reportFailed]);

  // Validate, enqueue and start a batch of picked files.
  const startFiles = (files: File[]) => {
    if (files.length === 0) return;
    const accepted: UploadRow[] = [];
    const rejected: string[] = [];
    for (const file of files) {
      if (!isSupported(file)) { rejected.push(`${file.name}: formato no soportado`); continue; }
      if (file.size > MAX_BYTES) { rejected.push(`${file.name}: excede 10 GB`); continue; }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        status: 'queued',
        progress: 0,
      });
    }
    if (rejected.length > 0) {
      toast({ variant: 'error', message: rejected.join(' · ') });
    }
    setQueue(prev => [...prev, ...accepted]);
    for (const item of accepted) {
      void uploadOne(item, defaultAssetType);
    }
  };

  const pickFiles = (list: FileList | null) => {
    if (!list) return;
    startFiles(Array.from(list));
  };

  // Persist a File System Access handle for each multipart-sized file so a
  // future reload can re-acquire read access with a single permission grant.
  const rememberHandles = (picked: Array<{ file: File; handle: unknown }>) => {
    for (const { file, handle } of picked) {
      if (handle && shouldUseMultipart(file)) void saveFileHandle(mpuKey(file), handle);
    }
  };

  // Dropzone click → File System Access picker (Chromium) so handles persist for
  // reload-resume; otherwise the plain hidden <input>.
  const openFilePicker = async () => {
    if (!supportsFsAccess()) { fileInputRef.current?.click(); return; }
    try {
      const handles: any[] = await (window as any).showOpenFilePicker({ multiple: true });
      const picked = await Promise.all(handles.map(async (h) => ({ file: (await h.getFile()) as File, handle: h })));
      rememberHandles(picked);
      startFiles(picked.map(p => p.file));
    } catch { /* user dismissed the picker */ }
  };

  // Drag-drop → capture FS Access handles when the browser offers them
  // (getAsFileSystemHandle must be kicked off synchronously while items live).
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setDrag(false);
    const dt = e.dataTransfer;
    if (supportsFsAccess() && dt.items && dt.items.length) {
      const handlePromises = Array.from(dt.items)
        .filter(it => it.kind === 'file' && typeof (it as any).getAsFileSystemHandle === 'function')
        .map(it => (it as any).getAsFileSystemHandle() as Promise<any>);
      if (handlePromises.length) {
        try {
          const handles = await Promise.all(handlePromises);
          const picked = await Promise.all(
            handles.filter(h => h && h.kind === 'file').map(async (h) => ({ file: (await h.getFile()) as File, handle: h })),
          );
          if (picked.length) { rememberHandles(picked); startFiles(picked.map(p => p.file)); return; }
        } catch { /* fall through to plain files */ }
      }
    }
    pickFiles(dt.files);
  };

  // ── Resume interrupted multipart uploads recovered from localStorage ────────
  // After a reload the queue (and each File handle) is gone, but incomplete
  // multipart sessions survive in localStorage. We surface them and let the
  // operator continue — via a persisted FS Access handle (one grant) or a
  // manual re-select — from the parts already sent.
  const [pending, setPending] = useState<PendingMpuUpload[]>(() => listPendingMpuUploads());
  const resumeInputRef = useRef<HTMLInputElement | null>(null);
  const resumeTargetRef = useRef<PendingMpuUpload | null>(null);

  // Hide any pending entry whose file is already active in the queue this
  // session (freshly resumed or still uploading) — it shows as a queue row.
  const visiblePending = pending.filter(
    p => !queue.some(r => fileMatchesMpuKey(r.file, p.key)),
  );

  const resumeWithFile = useCallback((file: File, target: PendingMpuUpload) => {
    const row: UploadRow = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      status: 'queued',
      progress: target.partCount > 0 ? Math.round((target.partsDone / target.partCount) * 100) : 0,
    };
    setQueue(prev => [...prev, row]);
    void uploadOne(row, target.assetType as AssetType);
  }, [uploadOne]);

  const beginResume = useCallback(async (p: PendingMpuUpload) => {
    // Chromium: a persisted handle re-acquires the bytes with ONE permission
    // grant — no file-picker navigation. Fall back to manual re-select if the
    // handle is missing (other browsers / drag without handle) or denied.
    try {
      const handle: any = await loadFileHandle(p.key);
      if (handle) {
        const q = (await handle.queryPermission?.({ mode: 'read' })) ?? 'prompt';
        const granted = q === 'granted' ? 'granted' : await handle.requestPermission({ mode: 'read' });
        if (granted === 'granted') {
          const file: File = await handle.getFile();
          if (fileMatchesMpuKey(file, p.key)) { resumeWithFile(file, p); return; }
        }
      }
    } catch { /* fall through to manual re-select */ }
    resumeTargetRef.current = p;
    resumeInputRef.current?.click();
  }, [resumeWithFile]);

  const onResumeFile = useCallback((list: FileList | null) => {
    const target = resumeTargetRef.current;
    resumeTargetRef.current = null;
    if (!target || !list || list.length === 0) return;
    const file = list[0];
    // The re-attached file must be the exact bytes the session was created for,
    // else the resume key won't match and it would silently start a new upload.
    if (!fileMatchesMpuKey(file, target.key)) {
      toast({ variant: 'error', message: `Selecciona el mismo archivo para reanudar: ${target.name}` });
      return;
    }
    resumeWithFile(file, target);
  }, [toast, resumeWithFile]);

  const dismissPending = useCallback((key: string) => {
    discardPendingMpuUpload(key);
    void deleteFileHandle(key);
    setPending(prev => prev.filter(p => p.key !== key));
  }, []);

  // Per-row cancel: stop the in-flight PUTs, tear down the S3 multipart upload,
  // drop the resume session + handle, and mark the asset failed. Distinct from a
  // navigation pause (which keeps everything for resume).
  const cancelRow = useCallback((row: UploadRow) => {
    canceledRef.current.add(row.id);
    const h = xhrsRef.current.get(row.id);
    xhrsRef.current.delete(row.id);
    try { h?.abort(); } catch { /* already settled */ }
    patch(row.id, { status: 'error', errorMessage: 'Subida cancelada.' });
    void (async () => {
      await abortMultipart(row.file); // no-op for single-PUT (no session)
      if (row.assetId) await reportFailed(row.assetId, 'Subida cancelada.');
    })();
  }, [reportFailed]);

  // An upload is "in flight" while any row is mid-pipeline. Leaving now would
  // silently kill the browser→S3 PUT, so we guard navigation in both
  // directions: in-app (useBlocker) and tab-close/refresh/external (beforeunload).
  const uploadInFlight = queue.some(
    r => r.status === 'creating' || r.status === 'uploading' || r.status === 'completing',
  );

  // Abort every live XHR. Each abort rejects its putWithProgress promise →
  // uploadOne's catch flips the UI row to error AND fires markUploadFailed.
  const cancelInFlight = useCallback(() => {
    for (const xhr of xhrsRef.current.values()) {
      try { xhr.abort(); } catch { /* already settled */ }
    }
    xhrsRef.current.clear();
  }, []);

  // In-app navigation guard (react-router data router). Blocks only while an
  // upload is in flight; the ConfirmModal below resolves the block.
  const blocker = useBlocker(uploadInFlight);

  // Tab close / refresh / external navigation: native "Leave site?" prompt.
  // Text can't be customised in modern browsers; the in-app modal carries the
  // real message.
  useEffect(() => {
    if (!uploadInFlight) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [uploadInFlight]);

  // Unmount while uploading (route swap that wasn't blocked, parent teardown):
  // abort the XHRs so we don't leak a PUT. The catch path reports each row.
  useEffect(() => {
    return () => { cancelInFlight(); };
  }, [cancelInFlight]);

  return (
    <div className="circa-cols-8-4">
      <div>
        <PageHead title="Subir contenido" description="Arrastra videos para añadirlos a tu biblioteca." />
        <div
          onClick={() => { void openFilePicker(); }}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { void handleDrop(e); }}
          style={{ border: `2px dashed ${drag ? 'var(--cta)' : 'var(--border-cool)'}`, borderRadius: 22, padding: '56px 24px', textAlign: 'center', cursor: 'pointer', background: drag ? 'var(--pale-blue)' : 'var(--canvas)', transition: 'all .15s' }}
        >
          <Icon name="uploadCloud" size={48} color="var(--muted-slate)" />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400, margin: '14px 0 6px' }}>{drag ? 'Suelta para subir' : 'Arrastra tus videos aquí'}</div>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--muted-slate)' }}>MP4, MOV, MKV, AVI, WebM o MPEG. Hasta 10 GB por archivo.</p>
          <Btn variant="ghost">Explorar archivos…</Btn>
          <input ref={fileInputRef} type="file" accept="video/*,.mkv,.mpeg,.mpg" multiple style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={e => { pickFiles(e.target.files); e.target.value = ''; }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
          <Field label="Tipo de contenido por defecto">
            <Select
              value={defaultAssetType}
              onChange={(v: string) => setDefaultAssetType(v as AssetType)}
              full
              options={ASSET_TYPE_OPTIONS.map(([value, label]) => ({ value, label }))}
            />
          </Field>
        </div>
        <input ref={resumeInputRef} type="file" accept="video/*,.mkv,.mpeg,.mpg" style={{ display: 'none' }}
          onChange={e => { onResumeFile(e.target.files); e.target.value = ''; }} />
        {visiblePending.length > 0 && (
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted-slate)' }}>
              Subidas interrumpidas — vuelve a seleccionar el mismo archivo para reanudar
            </div>
            {visiblePending.map(p => (
              <Panel key={p.key} pad={14}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Icon name="uploadCloud" size={22} color="var(--muted-slate)" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted-slate)' }}>
                      {p.partsDone}/{p.partCount} partes subidas · {formatBytes(p.size)}
                    </div>
                  </div>
                  <Btn variant="ghost" onClick={() => beginResume(p)}>Reanudar</Btn>
                  <Btn variant="ghost" onClick={() => dismissPending(p.key)}>Descartar</Btn>
                </div>
              </Panel>
            ))}
          </div>
        )}
        {queue.length > 0 && (
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {queue.map(r => {
              const isErr = r.status === 'error';
              const isDone = r.status === 'done';
              const showPct = r.status === 'uploading';
              const statusLabel = showPct ? `${r.progress}%` : STATUS_LABEL[r.status];
              const barColor = isErr ? 'var(--bad)' : isDone ? 'var(--good)' : 'var(--cta)';
              const barValue = isDone || r.status === 'completing' ? 100 : r.progress;
              return (
                <Panel key={r.id} pad={14}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    <div style={{ width: 80, flexShrink: 0 }}><Thumb radius={8} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.file.name}</span>
                        <span className="t-mono" style={{ fontSize: 11, color: isErr ? 'var(--bad)' : 'var(--muted-slate)' }}>{statusLabel}</span>
                      </div>
                      <Progress value={barValue} color={barColor} />
                      {r.errorMessage && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--bad)', wordBreak: 'break-word' }}>{r.errorMessage}</div>}
                      {isDone && r.assetId && (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted-slate)' }}>
                          Procesando en AWS Batch · <a style={{ color: 'var(--cta)', textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); navigate('/library/' + r.assetId); }} href={`/library/${r.assetId}`}>Ver en biblioteca</a>
                        </div>
                      )}
                    </div>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{formatBytes(r.file.size)}</span>
                    {(r.status === 'creating' || r.status === 'uploading' || r.status === 'completing') && (
                      <Btn variant="ghost" onClick={() => cancelRow(r)}>Cancelar</Btn>
                    )}
                  </div>
                </Panel>
              );
            })}
          </div>
        )}
      </div>
      <AccessGate perm="asset_create"><LinkImportPanel assetTypeOptions={ASSET_TYPE_OPTIONS} /></AccessGate>
      <ConfirmModal
        open={blocker.state === 'blocked'}
        onClose={() => {
          if (proceedingRef.current) { proceedingRef.current = false; return; }
          blocker.reset?.();
        }}
        onConfirm={() => { proceedingRef.current = true; cancelInFlight(); blocker.proceed?.(); }}
        title="Hay una subida en curso"
        body="Si sales ahora se cancelará la subida en progreso y el video no se procesará. ¿Salir igualmente?"
        confirmLabel="Salir y cancelar"
        danger
      />
    </div>
  );
}

/* ContentDetail — wired to api.assetQueries.getAssetById (a point read; do
 * NOT go back to scanning a bounded api.assets.list and `.find`-ing by id —
 * that was NMW-483). Honest-state contract: the Procesamiento panel
 * reads `transcodingStatus` directly; tabs that need backend data the
 * schema does not project (transcript / scenes / usage) render empty
 * states with a "pendiente" reason instead of fabricating rows. */
type RealAsset = {
  _id: string;
  title: string;
  assetType?: string;
  transcodingStatus: 'pending' | 'validating' | 'queued' | 'dispatched' | 'processing' | 'uploading' | 'finalizing' | 'completed' | 'failed' | 'dead' | 'removing' | 'remove_failed';
  durationMs?: number;
  createdAt?: number;
  resolution?: { width: number; height: number } | null;
  tags?: string[];
  thumbnailUrl?: string;
  playbackUrl?: string;
  transcodingError?: string;
  failureReason?: string;
  transcodingProgress?: number;
  transcodingStartedAt?: number;
  // Present on the raw doc returned by `assetQueries.getAssetById` (the
  // projected `assets.list` shape never carried it). ContentDetail uses it to
  // keep soft-deleted assets on the not-found branch — see NMW-483.
  trashedAt?: number;
  intake?: { stagedAt?: number; copyJobId?: string } | null;
};

const TRANSCODE_TONE: Record<RealAsset['transcodingStatus'], 'good' | 'warn' | 'bad' | 'neutral'> = {
  pending:       'warn',
  validating:  'warn',
  queued:      'warn',
  dispatched:  'warn',
  processing:  'warn',
  uploading:   'warn',
  finalizing:  'warn',
  completed:   'good',
  failed:      'bad',
  dead:        'bad',
  removing:      'warn',
  remove_failed: 'bad',
};
const TRANSCODE_LABEL: Record<RealAsset['transcodingStatus'], string> = {
  pending:       'Pendiente',
  validating:  'Validando origen',
  queued:      'En cola',
  dispatched:  'En edge',
  processing:  'Transcodificando',
  uploading:   'Subiendo a S3',
  finalizing:  'Subiendo salida',
  completed:   'Completo',
  failed:      'Falló',
  dead:        'Falló (sin reintentos)',
  removing:      'Eliminando',
  remove_failed: 'Falla al eliminar',
};

// NMW-237 — honest conversion-progress model. A real percentage is shown ONLY
// when the transcode worker has emitted `transcodingProgress` (wired end-to-end
// through the /edge/callback `transcoding_progress` event → updateProgress, then
// projected by api.assets.list). Until the worker emits it, `real` is undefined
// and the UI falls back to the real stage label + an elapsed-time hint — never a
// fabricated bar or a 0% placeholder (CLAUDE.md honest-state contract).
// Clamp+round a raw progress number to a finite 0-100 integer, or undefined when
// the value is absent/NaN/Infinity. Single source of truth for every % we render
// (detail bar, list row, grid overlay, analyse bar) so none can show a bogus or
// out-of-range percentage from an unbounded backend value.
function pct(n: number | undefined | null): number | undefined {
  return Number.isFinite(n as number) ? Math.max(0, Math.min(100, Math.round(n as number))) : undefined;
}

function progressView(a: { transcodingProgress?: number; transcodingStartedAt?: number }) {
  const real = pct(a.transcodingProgress);
  const elapsedMs = a.transcodingStartedAt ? Date.now() - a.transcodingStartedAt : undefined;
  const stale = elapsedMs !== undefined && elapsedMs > 15 * 60 * 1000; // 15m → likely stalled
  return { real, elapsedMs, stale };
}

// Client-computed transcode ETA. The UI OWNS this estimate: at mount it has no
// history, so ETA starts at 0 (not-ready) and only becomes meaningful after it
// has observed progress over time. Every second it samples the live percent,
// keeps a rolling 10s window, and extrapolates remaining time from the average
// rate across that window: rate = Δ% / Δs → etaSec = (100 − now) / rate. This
// is deliberately not derived from the backend — the backend has no wall-clock
// rate — so it self-corrects as the encode speeds up or slows down.
function useTranscodeEta(progressPct: number | undefined, active: boolean): { etaSec: number; ready: boolean } {
  const latest = useRef<number | undefined>(progressPct);
  latest.current = progressPct;
  const samples = useRef<Array<{ t: number; p: number }>>([]);
  const [state, setState] = useState<{ etaSec: number; ready: boolean }>({ etaSec: 0, ready: false });

  useEffect(() => {
    if (!active) {
      samples.current = [];
      setState({ etaSec: 0, ready: false });
      return;
    }
    const tick = () => {
      const now = Date.now();
      const p = latest.current;
      if (typeof p === 'number' && Number.isFinite(p)) {
        samples.current.push({ t: now, p });
        const cutoff = now - 10_000; // keep only the last 10 seconds
        samples.current = samples.current.filter((s) => s.t >= cutoff);
      }
      const s = samples.current;
      if (s.length >= 2) {
        const first = s[0];
        const last = s[s.length - 1];
        if (last.p >= 100) { setState({ etaSec: 0, ready: true }); return; }
        const dp = last.p - first.p;          // % gained across the window
        const dt = (last.t - first.t) / 1000; // seconds spanned
        if (dp > 0 && dt > 0) {
          const rate = dp / dt;               // %/s averaged over the last ≤10s
          const etaSec = Math.max(0, Math.round((100 - last.p) / rate));
          setState({ etaSec, ready: true });
          return;
        }
      }
      // Not enough data yet, or a momentary stall: hold a prior estimate rather
      // than flickering to 0; before the first estimate, stay at the 0 baseline.
      setState((prev) => (prev.ready ? prev : { etaSec: 0, ready: false }));
    };
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [active]);

  return state;
}

// Format an ETA in seconds as a compact human string ("45s", "3m 20s").
function fmtEta(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

// Format a phase duration (ms) to a compact human string. Sub-second phases
// keep millisecond precision so a fast Queue/Provisioning step isn't rounded
// to a misleading "0s"; anything ≥1s reuses the shared fmtDur vocabulary.
function fmtPhase(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return fmtDur(ms);
}

/**
 * NMW (EventBridge batch timing) — compact per-asset phase timeline.
 *
 * Reads every AWS Batch job for the asset (api.queries.awsBatchJobs.getByAssetId,
 * newest-first), picks the PRIMARY transcode job — excluding teaser/re-encode
 * correlation ids and not-yet-submitted `pending-` placeholder jobs — and renders
 * its derived `phaseDurations` (Queue, Provisioning, Transcode, Upload, Total).
 *
 * Honest-state contract: `undefined` ⇒ skeleton; no primary job ⇒ empty state;
 * an individual undefined phase ⇒ '—' (never a fabricated 0).
 */
function PhaseTimeline({ assetId }: { assetId: string }) {
  const jobs = useQuery(
    api.queries.awsBatchJobs.getByAssetId,
    isConvexId(assetId) ? { assetId: assetId as any } : 'skip',
  ) as Array<{
    _id: string;
    jobId: string;
    correlationId: string;
    status: string;
    phaseDurations?: {
      queueMs?: number;
      provisioningMs?: number;
      transcodeMs?: number;
      uploadMs?: number;
      totalMs?: number;
    };
  }> | undefined;

  if (jobs === undefined) {
    return <Panel title="Fases del procesamiento"><SkeletonList rows={5} /></Panel>;
  }

  // Query returns newest-first; the first non-teaser/non-reencode job with a
  // real (non-placeholder) AWS Batch job id is the primary transcode.
  const primary = jobs.find(
    (j) =>
      !j.correlationId.startsWith('teaser-') &&
      !j.correlationId.startsWith('reencode-') &&
      !j.jobId.startsWith('pending-'),
  );

  if (!primary) {
    return (
      <Panel title="Fases del procesamiento">
        <EmptyState icon="clock" title="Sin datos de fases" body="Aún no hay un trabajo de transcodificación con tiempos registrados para este video." />
      </Panel>
    );
  }

  const d = primary.phaseDurations;
  const phases: Array<[string, number | undefined]> = [
    ['En cola', d?.queueMs],
    ['Aprovisionamiento', d?.provisioningMs],
    ['Transcodificación', d?.transcodeMs],
    ['Subida', d?.uploadMs],
  ];

  return (
    <Panel title="Fases del procesamiento">
      {phases.map(([label, ms]) => (
        <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)', fontSize: 14 }}>
          <span style={{ color: 'var(--muted-slate)' }}>{label}</span>
          {ms === undefined
            ? <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>—</span>
            : <span className="t-mono" style={{ fontSize: 13 }}>{fmtPhase(ms)}</span>}
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', fontSize: 14, fontWeight: 600 }}>
        <span>Total</span>
        {d?.totalMs === undefined
          ? <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>—</span>
          : <span className="t-mono" style={{ fontSize: 13 }}>{fmtPhase(d.totalMs)}</span>}
      </div>
    </Panel>
  );
}

// Scene list — the real analysis output (api.analysis.queries.getSceneSegments
// + getKeyframes) as a vertical list, one row per scene (owner request
// 2026-10-13: no grids). The component lives in ./SceneList so the asset
// page's "Escenas" tab (FramesTab's v1 fallback) and the Analyse screen share
// it; this name stays for the Analyse screen's call sites.
function SceneGallery({ assetId }: { assetId?: string }) {
  return <SceneList assetId={assetId} />;
}

/**
 * NMW-418 (Phase 4 slice) — "Transcodes" tab: lists every rendition of an asset
 * (the ABR multi-rendition model) in a small table. Reads
 * api.transcodes.getAssetTranscodes (auth-guarded); honest-state — undefined ⇒
 * skeleton, [] ⇒ empty state. Read-only for now; promote / re-transcode actions
 * arrive with Phase 2's dispatch.
 */
function TranscodesTab({ assetId }: { assetId: string }) {
  const data = useQuery(
    api.transcodes.getAssetTranscodes,
    assetId ? { assetId: assetId as any } : 'skip',
  ) as { activeTranscodeId: string | null; renditions: any[] } | undefined;

  if (data === undefined) {
    return <Panel title="Transcodes"><SkeletonTable /></Panel>;
  }
  const { activeTranscodeId, renditions } = data;
  if (!renditions.length) {
    return (
      <Panel title="Transcodes">
        <EmptyState icon="film" title="Sin transcodes" body="Este asset todavía no tiene renditions de transcodificación." />
      </Panel>
    );
  }
  const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--muted-slate)', fontWeight: 500, borderBottom: '1px solid var(--lightest-gray)', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '8px 10px', fontSize: 13, borderBottom: '1px solid var(--lightest-gray)', whiteSpace: 'nowrap' };
  const fmtProfile = (p: any) =>
    p && (p.width || p.height)
      ? `${p.height}x${p.width}@${typeof p.frameRate === 'number' ? p.frameRate : 'null'}`
      : (p?.label ?? '—');
  const statusTone = (s: string) => (s === 'ready' ? 'good' : s === 'failed' ? 'bad' : 'neutral');

  return (
    <Panel title={`Transcodes (${renditions.length})`}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}></th>
              <th style={th}>Tipo</th>
              <th style={th}>Estado</th>
              <th style={th}>Perfil</th>
              <th style={th}>Duración</th>
              <th style={th}>Segmentos</th>
              <th style={th}>Prefijo S3</th>
              <th style={th}>Creado</th>
            </tr>
          </thead>
          <tbody>
            {renditions.map((r: any) => (
              <tr key={r._id}>
                <td style={td}>{r._id === activeTranscodeId ? <Pill tone="good" size="sm">Activo</Pill> : null}</td>
                <td style={td}>{r.kind}</td>
                <td style={td}><Pill tone={statusTone(r.status)} size="sm">{r.status}</Pill></td>
                <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)' }}>{fmtProfile(r.profile)}</td>
                <td style={td}>{typeof r.durationMs === 'number' ? fmtDur(r.durationMs) : '—'}</td>
                <td style={td}>{r.totalSegments ?? '—'}</td>
                <td style={{ ...td, fontFamily: 'var(--font-mono, monospace)', color: 'var(--muted-slate)', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.s3OutputPrefix}>{r.s3OutputPrefix}</td>
                <td style={td}>{r.createdAt ? ago(r.createdAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

/**
 * The deep-link second of `?t=<whole seconds>` (CONTRACTS.md section 9.10,
 * time rule 5: `deepLinkSecond = floor(ms / 1000)`), or `null` when the
 * parameter is absent or not a whole non-negative number (`abc`, `-1`, `4.5`).
 */
export function parseDeepLinkSecond(search: string): number | null {
  const raw = new URLSearchParams(search).get('t');
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const seconds = Number(raw);
  return Number.isSafeInteger(seconds) ? seconds : null;
}

/**
 * MA-82 (CONTRACTS.md section 9.10) — the current run's `originTicks` for the
 * player's descriptor, read from `mediaAnalysis.queries.getAnalysisWindow`
 * (an empty window of the current run: `kinds: []` reads no row, the answer
 * still carries `originTicks`). Published upward through an effect and kept
 * behind its own boundary, the `CoverageFetch` idiom: the query requires
 * `asset_update` and an active member, and a refused read must cost the
 * correction and nothing else — never the asset page.
 */
function OriginTicksProbe({ assetId, onOriginTicks }: { assetId: string; onOriginTicks: (ticks: number | undefined) => void }): null {
  const answer = useQuery(
    mediaAnalysisApi.queries.getAnalysisWindow,
    { assetId, startMs: 0, endMs: 1, kinds: [] } as any,
  ) as { originTicks: number | null } | undefined;
  const ticks = typeof answer?.originTicks === 'number' ? answer.originTicks : undefined;
  useEffect(() => { onOriginTicks(ticks); }, [ticks, onOriginTicks]);
  return null;
}

class OriginTicksBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  override componentDidCatch(error: unknown): void {
    // Keys, not values (V3I-INV-12). Without the origin the player seeks uncorrected (time rule 9).
    console.error('[ContentDetail] getAnalysisWindow failed; no origin correction', error);
  }
  override render(): React.ReactNode { return this.state.failed ? null : this.props.children; }
}

export function ContentDetail({ id }) {
  const { toast } = useToast();
  const [replace, setReplace] = useState(false);
  const [trash, setTrash] = useState(false);
  const [tab, setTab] = useState('overview');
  // MA-82 — the current run's origin P0 (section 9.10), for the descriptor.
  const [originTicks, setOriginTicks] = useState<number | undefined>(undefined);

  // MA-34 — the seek handle of the asset page's player. Analysis times (the
  // deep link, a transcript cue) seek through it so the player's origin
  // correction applies (section 9.10). The deep link is read once, from the
  // address at mount, and applied once, when the player reports it can play.
  const playerRef = useRef<AssetPlayerHandle | null>(null);
  const [deepLinkSecond] = useState<number | null>(() => parseDeepLinkSecond(window.location.search));
  const deepLinkApplied = useRef(false);
  const seekMs = useCallback((ms: number) => { playerRef.current?.seekMs(ms); }, []);
  const onPlayerLoaded = useCallback(() => {
    if (deepLinkSecond === null || deepLinkApplied.current) return;
    deepLinkApplied.current = true;
    seekMs(deepLinkSecond * 1000);
  }, [deepLinkSecond, seekMs]);
  // NMW-237: tick every 30s so the elapsed-time hint and the 15-min staleness
  // flag advance without waiting for a Convex data push (Date.now() is not
  // reactive). Cheap; only re-renders this detail view.
  const [, setNowTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // "Añadir a programación" (was a dead-end toast). Open a modal listing the
  // operator's schedule containers, pick one, then append THIS asset as a new
  // schedule item via the real `api.schedule.addScheduleItemToContainer`
  // mutation + recalc. Routed through the honest-error wrapper (FR-013 H1).
  const [addToScheduleOpen, setAddToScheduleOpen] = useState(false);
  const [pickedScheduleId, setPickedScheduleId] = useState<string>('');
  const schedulesForPick = useQuery(api.schedules.listSchedules, addToScheduleOpen ? {} : 'skip') as
    | Array<{ _id: string; name: string; status: string; channelId?: string }>
    | undefined;
  const addItemToScheduleM = useConvexMutationWithToast(api.schedule.addScheduleItemToContainer, {
    logLabel: 'schedule_addScheduleItemToContainer',
    successMessage: 'Añadido a la programación.',
    errorMessagePrefix: 'No se pudo añadir a la programación',
  });
  const recalcScheduleM = useConvexMutationWithToast(api.schedule.recalculateContainerStartTimes, {
    logLabel: 'schedule_recalculateContainerStartTimes',
    errorMessagePrefix: 'No se pudieron recalcular los horarios',
  });

  // Soft delete — move this asset to the trash (papelera). Keeps the DB row and
  // its S3 objects; the asset is restorable from /library/trash. Routed through
  // the honest-error wrapper (FR-013 H1).
  const moveToTrashM = useConvexMutationWithToast(api.assetQueries.moveToTrash, {
    logLabel: 'assets_moveToTrash',
    successMessage: 'Movido a la papelera.',
    errorMessagePrefix: 'No se pudo mover a la papelera',
  });

  // Operator-triggered retry for a failed/dead conversion. Re-fires the live
  // transcoder via api.mutations.assets.retryConversion. Routed through the
  // honest-error wrapper (FR-013 H1). NOT retry-safe — the button is disabled
  // while in flight to avoid double-submitting a transcode job.
  const retryConversionM = useConvexMutationWithToast(api.mutations.assets.retryConversion, {
    logLabel: 'assets_retryConversion',
    successMessage: 'Reintentando la conversión…',
    errorMessagePrefix: 'No se pudo reintentar la conversión',
  });

  // Operator-triggered RE-transcode for an already-completed asset (regenerates
  // /transcoded/<id>/). Distinct from retryConversion (failed-only). Sends the
  // asset back to "pending" and overwrites its transcoded output.
  const reconvertM = useConvexMutationWithToast(api.mutations.assets.reconvertAsset, {
    logLabel: 'assets_reconvertAsset',
    successMessage: 'Volviendo a convertir…',
    errorMessagePrefix: 'No se pudo volver a convertir',
  });

  // NMW-483 — point-read the asset. The previous shape scanned
  // `assets.list({ limit: 200 })` (server-clamped to MAX_LIMIT = 100) and
  // `.find`-ed client-side, so every asset outside the newest 100 rendered a
  // false "No encontrado". `undefined` = loading; `null` (after resolved) =
  // not-found.
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    isConvexId(id) ? { assetId: id as any } : 'skip',
  ) as RealAsset | undefined | null;
  // Two collapses onto the not-found branch:
  //  - a malformed id `'skip'`s the query, which would otherwise leave `aDoc`
  //    undefined (= skeleton) forever;
  //  - `getAssetById` is a bare `ctx.db.get` with no `trashedAt` filter, while
  //    the `assets.list` it replaced dropped soft-deleted rows. Trash.tsx
  //    (listTrashedAssets) stays the only surface that shows them, so a
  //    trashed id keeps rendering "No encontrado" here as it did pre-NMW-483.
  const a = !isConvexId(id) ? null
    : aDoc === undefined ? undefined
      : aDoc && !aDoc.trashedAt ? aDoc
        : null;

  // Real analysis/enrichment state for the "Procesamiento" panel pills.
  const sceneRows = useQuery(
    api.analysis.queries.getSceneSegments,
    isConvexId(id) ? { assetId: id as any } : 'skip',
  ) as Array<unknown> | undefined;
  const enrich = useQuery(
    api.analysis.queries.getEnrichmentStatus,
    isConvexId(id) ? { assetId: id as any } : 'skip',
  ) as { analysisStatus?: string; enrichmentResultCount?: number; enrichmentJobId?: string | null; currentRunId?: string | null } | undefined | null;

  // Client-side transcode ETA — must run unconditionally (before the early
  // returns) to satisfy the Rules of Hooks. Active only while the transcode is
  // in flight; the hook resets to the 0 baseline otherwise.
  const etaActive = !!a && ['pending', 'validating', 'queued', 'dispatched', 'processing', 'uploading', 'finalizing'].includes(a.transcodingStatus);
  const eta = useTranscodeEta(pct(a?.transcodingProgress), etaActive);

  if (a === undefined) {
    return (
      <div>
        <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }]} title="Cargando…" />
        <SkeletonPanel rows={6} />
      </div>
    );
  }
  if (a === null) {
    return (
      <div>
        <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }]} title="No encontrado" />
        <Panel><EmptyState icon="film" title="No encontramos este video" body="Puede haber sido movido a la papelera o nunca terminó de subir." action={<Btn variant="solid" onClick={() => navigate('/library')}>Volver a la biblioteca</Btn>} /></Panel>
      </div>
    );
  }

  const ready = a.transcodingStatus === 'completed';
  const failed = a.transcodingStatus === 'failed' || a.transcodingStatus === 'dead';
  const removing = a.transcodingStatus === 'removing' || a.transcodingStatus === 'remove_failed';
  const inProgress = !ready && !failed && !removing;
  const convProg = progressView(a); // NMW-237: real % or honest stage+elapsed fallback
  const resolutionLabel = a.resolution ? `${a.resolution.width}×${a.resolution.height}` : '—';
  const durationLabel = a.durationMs ? fmtDur(a.durationMs) : '—';

  // "Procesamiento" pills, derived from real backend state (no fabricated
  // "Completo"). Escenas reflects the scene-analysis pipeline; Transcripción /
  // Enriquecimiento reflect the heavy-plane enrichment pipeline.
  const analysisState = enrich?.analysisStatus ?? 'none';
  const enrichCount = enrich?.enrichmentResultCount ?? 0;
  const enrichBusy = !!enrich?.enrichmentJobId;
  const stagePill = (done: boolean, processing: boolean, failed: boolean) =>
    done ? { tone: 'good', text: 'Completado' }
      : failed ? { tone: 'bad', text: 'Falló' }
        : processing ? { tone: 'warn', text: 'Procesando' }
          : { tone: 'neutral', text: 'Pendiente' };
  // MA-82 / DEF-15: the "Transcripción" pill no longer reads Completado from
  // enrichment rows — since the 2026-08-20 rescope those rows hold captions
  // only, so a v1 analysis has no transcript to report (it is "Sin
  // transcripción", or "Procesando" while its enrichment runs). An asset with a
  // current v2 run (`currentRunId`) carries its four modality words in the
  // coverage chips above (`AssetCoverageDetail`), so the two v1 rows are not
  // rendered for it: one meaning per word, nothing duplicated.
  const v2Covered = !!enrich?.currentRunId;
  const procRows: Array<{ label: string; pill: { tone: string; text: string } }> = [
    { label: 'Escenas', pill: stagePill((sceneRows?.length ?? 0) > 0, analysisState === 'processing' || analysisState === 'pending', analysisState === 'failed') },
    ...(v2Covered ? [] : [
      { label: 'Transcripción', pill: enrichBusy ? { tone: 'warn', text: 'Procesando' } : { tone: 'neutral', text: 'Sin transcripción' } },
      { label: 'Enriquecimiento', pill: stagePill(enrichCount > 0, enrichBusy, false) },
    ]),
  ];

  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title }]} title={a.title}
        actions={<>
          {failed && (
            <AccessGate perm="asset_update">
              <Btn variant="solid" icon="refresh" loading={retryConversionM.loading} disabled={retryConversionM.loading}
                onClick={() => { void retryConversionM.run({ assetId: a._id as any }); }}>Reintentar conversión</Btn>
            </AccessGate>
          )}
          {ready && (
            <AccessGate perm="asset_update">
              <Btn variant="ghost" icon="refresh" loading={reconvertM.loading} disabled={reconvertM.loading}
                onClick={() => { void reconvertM.run({ assetId: a._id as any }); }}>Volver a convertir</Btn>
            </AccessGate>
          )}
          <AccessGate perm="schedule_create"><Btn variant="ghost" icon="calendar" onClick={() => { setPickedScheduleId(''); setAddToScheduleOpen(true); }}>Añadir a programación</Btn></AccessGate>
          <Btn variant="ghost" icon="zap" onClick={() => navigate('/library/' + a._id + '/markers')}>Marcar cortes</Btn>
          <Btn variant="ghost" icon="sparkles" onClick={() => navigate('/library/' + a._id + '/analyse')}>Analizar video</Btn>
          <Dropdown trigger={<Btn variant="ghost" icon="moreH" />} items={[
            { label: 'Editar metadatos', icon: 'edit', onClick: () => navigate('/library/' + a._id + '/metadata') },
            { label: 'Administrar subtítulos', icon: 'fileText', onClick: () => navigate('/library/' + a._id + '/captions') },
            { label: 'Reemplazar archivo', icon: 'refresh', onClick: () => setReplace(true) },
            { divider: true }, { label: 'Mover a la papelera', icon: 'trash', danger: true, onClick: () => setTrash(true) }]} />
        </>} />
      {!ready && (
        <FreshnessBanner show message={inProgress
          ? `Este video se está procesando (${intakePhaseLabel(a) ?? TRANSCODE_LABEL[a.transcodingStatus]}${convProg.real !== undefined ? `, ${convProg.real}%` : ''}). Algunas acciones estarán disponibles al terminar.`
          : [
              'La conversión falló. Reemplaza el archivo o reintenta.',
              a.failureReason ? ` Motivo: ${a.failureReason}.` : '',
              a.transcodingError ? ` Detalle: ${a.transcodingError}` : '',
            ].join('')} />
      )}
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {ready && a.playbackUrl
            ? <React.Suspense fallback={<Panel><SkeletonPanel h={240} /></Panel>}><AssetPlayer
                playerRef={playerRef}
                onLoaded={onPlayerLoaded}
                descriptor={{
                  assetId: a._id,
                  mediaKind: 'video',
                  source: { url: a.playbackUrl, contentType: 'application/x-mpegURL' },
                  title: a.title,
                  posterUrl: a.thumbnailUrl ?? undefined,
                  // The QoE beacon scope; the asset page has no channel (the
                  // same shape MarkerEditorPage uses, `marker-editor:<id>`).
                  channelId: `asset-page:${a._id}`,
                  durationMs: a.durationMs ?? 0,
                  // MA-82: the current run's origin P0 from `getAnalysisWindow`
                  // (section 9.10), so the player corrects analysis seeks and
                  // side-loaded cues (time rule 9). Absent without a run.
                  ...(originTicks !== undefined ? { originTicks } : {}),
                  // Side-loaded published subtitles ("Español (automático)")
                  // need a public URL the queries do not carry yet (card MA-84
                  // builds the WebVTT in the browser); the descriptor carries none.
                  textTracks: [],
                }} /></React.Suspense>
            : <Panel><EmptyState icon="film" title={ready ? 'Reproductor no disponible' : 'Aún no se puede reproducir'} body={ready ? 'El video terminó de procesarse pero no tiene URL de reproducción.' : 'Termina el procesamiento para previsualizar.'} /></Panel>}
          <div>
            <Tabs tabs={[{ id: 'overview', label: 'Resumen' }, { id: 'transcript', label: 'Transcripción' }, { id: 'scenes', label: 'Escenas' }, { id: 'audio', label: 'Audio' }, { id: 'usage', label: 'Uso' }, { id: 'transcodes', label: 'Transcodes' }]} active={tab} onChange={setTab} style={{ marginBottom: 20 }} />
            {tab === 'overview' && (
              <Panel title="Acerca de este video">
                {/* NMW-410: first-class inline tag editing. Self-persisting
                    <TagEditor> seeds from the asset's typed tags and writes
                    each add/remove immediately via applyAssetTags — no more
                    "edit elsewhere" hand-off. */}
                <div style={{ marginBottom: 20 }}>
                  <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 10 }}>Etiquetas</div>
                  <TagEditor assetId={a._id as string} />
                </div>
                <div style={{ borderTop: '1px solid var(--lightest-gray)', paddingTop: 18 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Miniatura</div>
                  {a.thumbnailUrl
                    ? <img src={a.thumbnailUrl} alt="" style={{ maxWidth: '100%', borderRadius: 12 }} />
                    : <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>La miniatura aparecerá cuando termine la conversión.</div>}
                </div>
              </Panel>
            )}
            {/* NMW-1166 — the real time-coded transcript. The placeholder that
                stood here said "sin transcripción" for EVERY asset because no
                query was ever issued; <TranscriptTab> reads
                api.queries.captionData.getTimedTranscript and renders its own
                Panel plus all four honest states (skeleton / never-analysed /
                no-speech / error toast). */}
            {tab === 'transcript' && <TranscriptTab assetId={a._id as string} onSeekMs={seekMs} />}
            {/* E-66/E-67 — one list: the scenes of the v2 run, each collapsed over its sublist of analysed
                frames, with the extract funnel; an asset without a v2 run gets its v1 scenes as a list.
                <FramesTab> reads mediaAnalysis.queries.listRunFrames and renders its own Panel and honest states. */}
            {tab === 'scenes' && <FramesTab assetId={a._id as string} onSeekMs={seekMs} />}
            {tab === 'audio' && <AudioVersionTab asset={a as any} />}
            {tab === 'usage' && (
              <Panel title="Programaciones que lo usan">
                <EmptyState icon="calendar" title="Sin programaciones" body="Aún no hay un servicio que liste las programaciones que referencian este contenido." />
              </Panel>
            )}
            {tab === 'transcodes' && <TranscodesTab assetId={a._id as string} />}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Detalles">
            {([
              ['Estado', intakePhaseLabel(a) ?? TRANSCODE_LABEL[a.transcodingStatus], 'live'],
              ['Tipo', a.assetType ?? '—', a.assetType ? 'live' : 'absent'],
              ['Duración', durationLabel, a.durationMs ? 'live' : 'absent', 'assets.durationMs es nulo en la base de datos (el medio sí tiene duración; no se persistió al ingerir).'],
              ['Resolución', resolutionLabel, a.resolution ? 'live' : 'absent', 'assets.resolution es nulo en la base de datos (no se persistió al ingerir).'],
              ['Añadido', a.createdAt ? ago(a.createdAt) : '—', a.createdAt ? 'live' : 'absent'],
            ] as Array<[string, string, SourceKind, string?]>).map(([k, v, kind, detail]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--lightest-gray)', fontSize: 14 }}>
                <span style={{ color: 'var(--muted-slate)' }}>{k}</span>
                <Provenance kind={kind} detail={detail}><span>{v}</span></Provenance>
              </div>
            ))}
          </Panel>
          <Panel title="Procesamiento">
            <div style={{ padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 14 }}>Conversión</span>
                <Pill tone={TRANSCODE_TONE[a.transcodingStatus]} size="sm" dot={inProgress}>
                  {intakePhaseLabel(a) ?? TRANSCODE_LABEL[a.transcodingStatus]}{convProg.real !== undefined ? ` · ${convProg.real}%` : ''}
                </Pill>
              </div>
              {/* NMW-237: real bar only when the worker emitted a %, otherwise an
                  honest stage+elapsed hint (and a staleness note) — no fake bar. */}
              {inProgress && convProg.real !== undefined && (
                <div style={{ marginTop: 8 }}>
                  <Progress value={convProg.real} h={6} color="var(--deep-green)" />
                  <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--muted-slate)' }}>
                    <span className="t-mono">{convProg.real}%</span>
                    <span className="t-mono">
                      ETA {eta.ready ? (eta.etaSec > 0 ? `~${fmtEta(eta.etaSec)}` : 'casi listo') : 'calculando…'}
                    </span>
                  </div>
                </div>
              )}
              {inProgress && convProg.real === undefined && (
                <div style={{ marginTop: 6, fontSize: 12, color: convProg.stale ? 'var(--warn)' : 'var(--muted-slate)' }}>
                  {a.transcodingStartedAt
                    ? (convProg.stale
                        ? `Sin novedades desde ${ago(a.transcodingStartedAt)} — puede estar atascado`
                        : `En proceso · ${ago(a.transcodingStartedAt)}`)
                    : 'En proceso…'}
                </div>
              )}
            </div>
            {/* NMW-1170 — the same coverage state the library list shows, so a
                badge seen there is explainable here, with the FULL failure
                reason (`analysisJobs.failureReason`) instead of the table's
                truncated one. A failed analysis is never shown as an
                unattempted one. MA-82: for an asset with a current v2 run the
                badge is its four modality chips. */}
            <AssetCoverageDetail assetId={a._id as string} />
            <OriginTicksBoundary><OriginTicksProbe assetId={a._id as string} onOriginTicks={setOriginTicks} /></OriginTicksBoundary>
            {/* Derived from real backend state: getSceneSegments (Escenas) and
                getEnrichmentStatus (Transcripción / Enriquecimiento). */}
            {procRows.map(({ label, pill }) => (
              <div key={label} data-testid="processing-row" data-label={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
                <span style={{ fontSize: 14 }}>{label}</span>
                <Pill tone={pill.tone as any} size="sm">{pill.text}</Pill>
              </div>
            ))}
          </Panel>
          <PhaseTimeline assetId={a._id as string} />
          <Panel title="Pistas de subtítulos" right={<Btn size="sm" variant="text" onClick={() => navigate('/library/' + a._id + '/captions')}>Administrar</Btn>}>
            <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>Los subtítulos se gestionan en una página dedicada.</div>
          </Panel>
        </div>
      </div>
      <ReplaceFileModal open={replace} onClose={() => setReplace(false)} assetId={a._id} />
      <TrashModal open={trash} onClose={() => setTrash(false)} count={1}
        onConfirm={() => { void moveToTrashM.run({ assetId: a._id as any }).then(() => navigate('/library'), () => {}); }} />
      <Modal
        open={addToScheduleOpen}
        onClose={() => setAddToScheduleOpen(false)}
        title="Añadir a programación"
        width={520}
        footer={<>
          <Btn variant="ghost" onClick={() => setAddToScheduleOpen(false)}>Cancelar</Btn>
          <Btn
            variant="solid"
            disabled={!pickedScheduleId || addItemToScheduleM.loading}
            loading={addItemToScheduleM.loading || recalcScheduleM.loading}
            onClick={async () => {
              if (!pickedScheduleId) return;
              try {
                await addItemToScheduleM.run({
                  scheduleContainerId: pickedScheduleId as any,
                  assetId: a._id,
                  title: a.title,
                  durationMs: a.durationMs ?? 0,
                  startTime: 0,
                  sourceUrl: a.playbackUrl ?? undefined,
                  thumbnailUrl: a.thumbnailUrl ?? undefined,
                });
                await recalcScheduleM.run({
                  scheduleContainerId: pickedScheduleId as any,
                  baseStartTime: 0,
                });
                setAddToScheduleOpen(false);
                navigate('/schedules/' + pickedScheduleId + '/edit');
              } catch {
                // honest wrapper already toasted; keep the modal open to retry.
              }
            }}
          >
            Añadir
          </Btn>
        </>}
      >
        {schedulesForPick === undefined ? (
          <SkeletonList rows={4} />
        ) : schedulesForPick.length === 0 ? (
          <EmptyState
            icon="calendar"
            title="Sin programaciones"
            body="Crea una programación primero para poder añadir contenido."
          />
        ) : (
          <Field label="Elige una programación">
            <Select
              value={pickedScheduleId}
              onChange={setPickedScheduleId}
              full
              options={schedulesForPick.map((s) => ({ value: s._id, label: s.name }))}
            />
          </Field>
        )}
      </Modal>
    </div>
  );
}

// Wired to the real analysis pipeline: api.analysis.mutations.analyzeAsset
// kicks off a durable job; api.analysis.queries.getAnalysisJobStatus is the
// live subscription that drives the status pill + progress; the completed
// counts come from the real segment/keyframe queries. No fabricated stats,
// no setInterval theatre. (Forward-ported from frontend-v2 AnalyseVideoPage.)
const ANALYSE_STAGE_LABEL: Record<string, string> = {
  PENDING: 'En cola',
  DOWNLOADING: 'Descargando origen',
  DETECTING: 'Detectando escenas',
  EXTRACTING_AUDIO: 'Segmentando audio',
  EXTRACTING_KEYFRAMES: 'Extrayendo fotogramas',
  UPLOADING: 'Subiendo resultados',
  COMPLETED: 'Completado',
  FAILED: 'Falló',
};
/* ============================================================
   MA-82 — the v2 analysis panel (CONTRACTS.md section 16.2 "Analysis
   panel", section 9.5, section 9.10 "Honest state", section 9.14).

   Driven by `mediaAnalysis.queries.getAnalysisStatus` ({ current, latest }):
   the stage stepper (Extracción → Comprensión → Subtítulos e índice →
   Persistencia) and the four modality chips describe the LATEST run (of any
   status); the stale banner describes the CURRENT run (section 9.5); the
   measured cost and the settings are the run's own, never computed here.
   "Analizar" keeps calling `analysis.mutations.analyzeAsset`, which starts a
   v2 run when the flag is "v2"; "Opciones" calls `workflow.analyzeAssetV2`
   with a profile and a locale; "Reintentar" passes `retryOf` on a failed or
   cancelled run; "Cancelar" calls `runs.cancelRun` on a running one. The
   three are admins only: DISABLED with a tooltip for anyone else, never
   hidden. A manual run refused by the daily ceiling (`ManualCeilingReached`)
   shows the toast "Se alcanzó el límite diario de análisis manuales".
   An asset with no v2 run keeps the v1 panel below, unchanged.
   ============================================================ */

const RUN_STAGES: ReadonlyArray<readonly [string, string]> = [
  ['extract', 'Extracción'],
  ['understand', 'Comprensión'],
  ['caption', 'Subtítulos e índice'],
  ['persist', 'Persistencia'],
];
const RUN_PROFILES = ['sparse', 'mixed', 'news', 'sports', 'music'] as const;
const RUN_LOCALES: ReadonlyArray<readonly [string, string]> = [['es', 'Español'], ['pt', 'Portugués'], ['en', 'Inglés']];
/** Failure reasons in the console's Spanish (section 16.2 "Words on screen"). */
const RUN_FAILURE_WORDS: Record<string, string> = {
  frames_failed: 'Fallo en fotogramas',
  transcript_failed: 'Fallo en transcripción',
  captions_failed: 'Fallo en subtítulos',
  index_failed: 'Fallo en el índice',
  rendition_changed: 'Archivo reemplazado',
  asset_removed: 'Recurso eliminado',
  operator_cancelled: 'Cancelado por un operador',
  run_timeout: 'Tiempo agotado',
};
const RUN_STATUS_WORDS: Record<string, { tone: string; text: string }> = {
  running: { tone: 'warn', text: 'En curso' },
  completed: { tone: 'good', text: 'Completado' },
  failed: { tone: 'bad', text: 'Falló' },
  cancelled: { tone: 'neutral', text: 'Cancelado' },
};
const NOT_ADMIN_TOOLTIP = 'Solo un administrador puede usar esta acción.';
const RESOLVING_TOOLTIP = 'Comprobando permisos…';
const MANUAL_CEILING_TOAST = 'Se alcanzó el límite diario de análisis manuales';
/**
 * The refusals a start can answer with, by the code the server's error carries, and the sentence each gets. The
 * wrapper (`useMutationWithToast`, locked) has already shown the server's text; the sentence here is the one a
 * person can act on. An unknown error gets no second toast.
 */
const START_REFUSAL_TOASTS: ReadonlyArray<[code: string, message: string]> = [
  ['ManualCeilingReached', MANUAL_CEILING_TOAST],
  ['pipeline_unconfigured', 'El análisis v2 no está configurado en este entorno: faltan las variables MEDIA_ANALYSIS_* del despliegue.'],
  ['pipeline_inactive', 'El análisis v2 está desactivado (analysisPipelineVersion no es "v2").'],
  ['asset_not_ready', 'El video aún no tiene una conversión terminada; no se puede analizar todavía.'],
  ['rendition_missing', 'La conversión indicada no existe o no está lista.'],
];
const STALE_RUN_MESSAGE = 'Análisis de una versión anterior del archivo. El análisis vigente se hizo sobre una versión anterior del archivo; al promover una versión nueva se inicia un reemplazo automáticamente.';

type RunStatusShape = {
  runId: string;
  status: string;
  stage: string;
  stageState: string;
  modalities: Record<'frames' | 'transcript' | 'captions' | 'index', string>;
  failureReason: string | null;
  settings: { profile?: string; speechConfiguration?: string; proseRate?: number; captionProvider?: string; captionModel?: string; captionServiceTier?: string; promptVersion?: string } | null;
  analysisLocale: string | null;
  measuredCostUsd: number | null;
  originTicks: number | null;
  createdAt: number;
  completedAt: number | null;
  stale: boolean;
};
type AnalysisStatusShape = { current: RunStatusShape | null; latest: RunStatusShape | null };
type StepState = 'done' | 'active' | 'failed' | 'cancelled' | 'pending';

/** The state of each of the four stages for the run's status, stage and stage state. */
export function runStepStates(run: Pick<RunStatusShape, 'status' | 'stage' | 'stageState'>): StepState[] {
  const at = RUN_STAGES.findIndex(([stage]) => stage === run.stage);
  return RUN_STAGES.map((_, i) => {
    if (run.status === 'completed') return 'done';
    if (at < 0) return 'pending'; // a stage this build does not know: nothing is claimed done
    if (i < at) return 'done';
    if (i > at) return 'pending';
    if (run.status === 'failed') return 'failed';
    if (run.status === 'cancelled') return 'cancelled';
    return 'active';
  });
}

const STEP_WORDS: Record<StepState, string> = { done: 'listo', active: 'en curso', failed: 'falló', cancelled: 'cancelado', pending: 'pendiente' };
const STEP_TONES: Record<StepState, string> = { done: 'good', active: 'warn', failed: 'bad', cancelled: 'neutral', pending: 'neutral' };

function runFailureWord(reason: string | null): string {
  if (!reason) return 'Motivo no registrado';
  return RUN_FAILURE_WORDS[reason] ?? `Motivo desconocido (${reason})`;
}

/**
 * The run's status read throws for a member without `asset_update` (section
 * 9.7); the page then shows an honest error instead of the root error surface.
 */
class AnalysisStatusBoundary extends React.Component<{ children: React.ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } { return { failed: true }; }
  override componentDidCatch(error: unknown): void {
    console.error('[Analyse] analysis status query failed', error); // keys, not values
  }
  override render(): React.ReactNode {
    if (this.state.failed) {
      return <Panel><EmptyState icon="alertTriangle" title="No se pudo leer el estado del análisis" body="La consulta al backend falló o no tienes permiso para ver el análisis. Esto NO significa que el video no esté analizado." /></Panel>;
    }
    return this.props.children;
  }
}

export function Analyse({ id }: { id?: string }) {
  return <AnalysisStatusBoundary key={id}><AnalyseInner id={id} /></AnalysisStatusBoundary>;
}

function AnalyseInner({ id }: { id?: string }) {
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    isConvexId(id) ? { assetId: id as any } : 'skip',
  ) as { _id?: string; title?: string } | undefined | null;
  const job = useQuery(
    api.analysis.queries.getAnalysisJobStatus,
    isConvexId(id) ? { assetId: id as any } : 'skip',
  ) as { status?: string; state?: string; progress?: number; error?: string } | undefined | null;
  const scenes = useQuery(api.analysis.queries.getSceneSegments, isConvexId(id) ? { assetId: id as any } : 'skip') as Array<unknown> | undefined;
  const audio = useQuery(api.analysis.queries.getAudioSegments, isConvexId(id) ? { assetId: id as any } : 'skip') as Array<{ silent?: boolean }> | undefined;
  const keyframes = useQuery(api.analysis.queries.getKeyframes, isConvexId(id) ? { assetId: id as any } : 'skip') as Array<unknown> | undefined;
  const { run: startAnalysis, loading: starting } = useConvexMutationWithToast(
    api.analysis.mutations.analyzeAsset,
    {
      logLabel: 'asset_analyze_start',
      successMessage: 'Análisis iniciado.',
      errorMessagePrefix: 'No se pudo iniciar el análisis',
    },
  );
  // MA-82 — the v2 runs of the asset, and the three admin-only controls.
  const runStatus = useQuery(
    mediaAnalysisApi.queries.getAnalysisStatus,
    isConvexId(id) ? { assetId: id as any } : 'skip',
  ) as AnalysisStatusShape | undefined;
  const isAdmin = useIsAdmin();
  const { toast } = useToast();
  const analyzeV2 = useConvexMutationWithToast(mediaAnalysisApi.workflow.analyzeAssetV2, {
    logLabel: 'media_analysis_analyze_v2',
    successMessage: 'Análisis iniciado.',
    errorMessagePrefix: 'No se pudo iniciar el análisis',
  });
  const cancelRunM = useConvexMutationWithToast(mediaAnalysisApi.runs.cancelRun, {
    logLabel: 'media_analysis_cancel_run',
    successMessage: 'Análisis cancelado.',
    errorMessagePrefix: 'No se pudo cancelar el análisis',
  });
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [profile, setProfile] = useState<string>('auto');
  const [locale, setLocale] = useState<string>('es');

  /**
   * A start or a cancel through the wrapper: a known refusal gets its own sentence (section 16.2), every failure
   * is answered `false` and never left as an unhandled rejection (the wrapper has toasted and logged it).
   */
  const attempt = async (run: (args: any) => Promise<unknown>, args: Record<string, unknown>): Promise<boolean> => {
    try {
      await run(args);
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const known = START_REFUSAL_TOASTS.find(([code]) => message.includes(code));
      if (known) toast({ variant: 'error', message: known[1] });
      return false;
    }
  };
  const runV2 = (args: Record<string, unknown>) => attempt(analyzeV2.run, args);
  const runV1 = (args: Record<string, unknown>) => attempt(startAnalysis, args);

  if (id && !isConvexId(id)) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  if (id && (aDoc === undefined || runStatus === undefined)) {
    return <Skeleton h={420} />;
  }
  if (id && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  const a = aDoc ?? { _id: '', title: '—' };

  const latest = runStatus?.latest ?? null;
  const current = runStatus?.current ?? null;
  const running = latest?.status === 'running';
  const terminal = latest?.status === 'failed' || latest?.status === 'cancelled';
  const controlsDisabled = isAdmin !== true;
  const controlTooltip = isAdmin === undefined ? RESOLVING_TOOLTIP : isAdmin ? undefined : NOT_ADMIN_TOOLTIP;

  // `null` job = never analysed; `undefined` = loading the job subscription.
  const jobStatus = job?.status ?? (job === null ? 'idle' : 'loading');
  const processing = jobStatus === 'processing' || jobStatus === 'pending';
  const completed = jobStatus === 'completed';
  const failed = jobStatus === 'failed';
  const pillTone = completed ? 'good' : failed ? 'bad' : processing ? 'warn' : 'neutral';
  const pillText = completed ? 'Completado' : failed ? 'Falló' : jobStatus === 'pending' ? 'En cola' : jobStatus === 'processing' ? 'Procesando' : jobStatus === 'loading' ? 'Cargando…' : 'Sin iniciar';

  const optionsModal = (
    <Modal
      open={optionsOpen}
      onClose={() => setOptionsOpen(false)}
      title="Opciones de análisis"
      width={480}
      footer={<>
        <Btn variant="ghost" onClick={() => setOptionsOpen(false)}>Cancelar</Btn>
        <Btn
          variant="solid"
          icon="sparkles"
          loading={analyzeV2.loading}
          disabled={!id || analyzeV2.loading}
          onClick={async () => {
            const ok = await runV2({ assetId: id, ...(profile === 'auto' ? {} : { profile }), analysisLocale: locale });
            if (ok) setOptionsOpen(false);
          }}
        >
          Iniciar análisis
        </Btn>
      </>}
    >
      <Field label="Perfil" hint="Automático: según la categoría del video. Un perfil fija el umbral de escenas y el ritmo de prosa.">
        <Select
          full
          value={profile}
          onChange={setProfile}
          options={[{ value: 'auto', label: 'Automático (según la categoría)' }, ...RUN_PROFILES.map((p) => ({ value: p, label: p }))]}
        />
      </Field>
      <Field label="Idioma del análisis">
        <Select full value={locale} onChange={setLocale} options={RUN_LOCALES.map(([value, label]) => ({ value, label }))} />
      </Field>
    </Modal>
  );

  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title ?? '—', href: '#/library/' + (a._id ?? '') }, { label: 'Analizar' }]} title="Analizar video" description={`${a.title ?? '—'} — convertir y analizar de forma automática.`} />
      {current?.stale && <FreshnessBanner show message={STALE_RUN_MESSAGE} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {latest ? (
          <div data-testid="analysis-run" data-run-status={latest.status}>
            <Panel
              title="Análisis"
              right={<Pill tone={(RUN_STATUS_WORDS[latest.status]?.tone ?? 'neutral') as any} dot={running}>{RUN_STATUS_WORDS[latest.status]?.text ?? `Estado desconocido (${latest.status})`}</Pill>}
            >
              <ol data-testid="analysis-stepper" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, listStyle: 'none', margin: '0 0 16px', padding: 0 }}>
                {runStepStates(latest).map((state, i) => (
                  <li key={RUN_STAGES[i][0]} data-testid="analysis-step" data-stage={RUN_STAGES[i][0]} data-state={state} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid var(--lightest-gray)', borderRadius: 12 }}>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{i + 1}</span>
                    <span style={{ fontSize: 13 }}>{RUN_STAGES[i][1]}</span>
                    <Pill tone={STEP_TONES[state] as any} size="sm" dot={state === 'active'}>
                      {state === 'active' ? (latest.stageState === 'queued' ? 'en cola' : latest.stageState === 'submitted' ? 'en ejecución' : STEP_WORDS[state]) : STEP_WORDS[state]}
                    </Pill>
                  </li>
                ))}
              </ol>
              <div style={{ marginBottom: 16 }}>
                <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 8 }}>Modalidades</div>
                <ModalityChips modalities={latest.modalities} />
              </div>
              {terminal && (
                <div data-testid="analysis-failure-reason" style={{ marginBottom: 16, fontSize: 13, color: 'var(--bad, #DC2626)' }}>
                  Motivo: {runFailureWord(latest.failureReason)}
                </div>
              )}
              <div className="circa-grid-3" style={{ marginBottom: 16 }}>
                <div data-testid="analysis-cost">
                  <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Costo medido</div>
                  <div style={{ fontSize: 14 }}>{typeof latest.measuredCostUsd === 'number' ? `US$ ${latest.measuredCostUsd.toFixed(4)}` : 'Se conoce al terminar'}</div>
                </div>
                <div data-testid="analysis-settings">
                  <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Configuración</div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    <div>Perfil: <span className="t-mono">{latest.settings?.profile ?? '—'}</span></div>
                    <div>Ritmo de prosa: <span className="t-mono">{typeof latest.settings?.proseRate === 'number' ? String(latest.settings.proseRate) : '—'}</span></div>
                    <div>Modelo de subtítulos: <span className="t-mono">{latest.settings?.captionModel ?? '—'}</span></div>
                    <div>Transcripción: <span className="t-mono">{latest.settings?.speechConfiguration ?? '—'}</span> · idioma <span className="t-mono">{latest.analysisLocale ?? '—'}</span></div>
                  </div>
                </div>
                <div data-testid="analysis-times">
                  <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>Tiempos</div>
                  <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                    <div>Iniciado {ago(latest.createdAt)}</div>
                    <div>{latest.completedAt ? `Terminado ${ago(latest.completedAt)}` : 'Sin terminar'}</div>
                    {current && current.runId !== latest.runId && <div style={{ color: 'var(--muted-slate)' }}>Análisis vigente: el completado {current.completedAt ? ago(current.completedAt) : '—'}.</div>}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Btn variant="solid" icon="sparkles" loading={starting} disabled={!id || running} onClick={() => { void runV1({ assetId: id }); }}>Re-analizar</Btn>
                <Btn variant="ghost" icon="sliders" disabled={controlsDisabled} title={controlTooltip} onClick={() => setOptionsOpen(true)}>Opciones</Btn>
                {terminal && (
                  <Btn variant="ghost" icon="refresh" loading={analyzeV2.loading} disabled={controlsDisabled || analyzeV2.loading} title={controlTooltip} onClick={() => { void runV2({ assetId: id, retryOf: latest.runId }); }}>Reintentar</Btn>
                )}
                {running && (
                  <Btn variant="danger" icon="x" loading={cancelRunM.loading} disabled={controlsDisabled || cancelRunM.loading} title={controlTooltip} onClick={() => { void attempt(cancelRunM.run, { runId: latest.runId }); }}>Cancelar</Btn>
                )}
              </div>
            </Panel>
            {current && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 24 }}>
                <div className="circa-grid-3">
                  <StatCard label="Escenas detectadas" value={scenes ? String(scenes.length) : '—'} icon="film" />
                  <StatCard label="Segmentos de audio" value={audio ? String(audio.length) : '—'} icon="activity" />
                  <StatCard label="Fotogramas clave" value={keyframes ? String(keyframes.length) : '—'} icon="grid" />
                </div>
                <Panel title="Escenas y fotogramas clave">
                  <SceneGallery assetId={id} />
                </Panel>
              </div>
            )}
          </div>
        ) : (
          <>
            <Panel title="Trabajo de análisis" right={<Pill tone={pillTone}>{pillText}</Pill>}>
              {processing && (
                <div style={{ marginBottom: 16 }}>
                  {/* NMW-237 honest-state: only show a bar when the job emits a real
                      numeric progress; otherwise the stage label below stands alone
                      (no fabricated 0% placeholder). */}
                  {pct(job?.progress) !== undefined && <Progress value={pct(job?.progress)} />}
                  {job?.state && <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 6 }}>{ANALYSE_STAGE_LABEL[job.state] ?? job.state}</div>}
                </div>
              )}
              <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--body-muted)' }}>El análisis automático genera transcripción, escenas, fotogramas clave y enriquecimiento semántico para búsqueda.</p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Btn variant="solid" icon="sparkles" loading={starting} disabled={!id || processing} onClick={() => { void runV1({ assetId: id }); }}>{completed || failed ? 'Re-analizar' : 'Iniciar análisis'}</Btn>
                <Btn variant="ghost" icon="sliders" disabled={controlsDisabled} title={controlTooltip} onClick={() => setOptionsOpen(true)}>Opciones</Btn>
              </div>
            </Panel>
            {failed && job?.error && (
              <Panel title="Detalle del fallo"><p style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.5 }}>{job.error}</p></Panel>
            )}
            {completed && (
              <>
                <div className="circa-grid-3">
                  <StatCard label="Escenas detectadas" value={scenes ? String(scenes.length) : '—'} icon="film" />
                  <StatCard label="Segmentos de audio" value={audio ? String(audio.length) : '—'} icon="activity" />
                  <StatCard label="Fotogramas clave" value={keyframes ? String(keyframes.length) : '—'} icon="grid" />
                </div>
                <Panel title="Escenas y fotogramas clave">
                  <SceneGallery assetId={id} />
                </Panel>
              </>
            )}
            {jobStatus === 'idle' && <Panel><EmptyState icon="sparkles" title="Aún no analizado" body="Inicia un análisis para generar transcripción, escenas y sugerencias de cortes." /></Panel>}
          </>
        )}
      </div>
      {optionsModal}
    </div>
  );
}

export function EditorialInbox() {
  const { toast } = useToast();
  // T318 — honest empty state. The real editorial inbox surface lives at
  // `src/screens/editorial/Inbox.tsx` and binds to its own Convex query;
  // this legacy bundle entry renders an honest EmptyState until any
  // remaining routes pointing here are migrated.
  const [items, setItems] = useState<Array<{ _id?: string; id?: string; title?: string; source?: string; durationMs?: number; addedAt?: number }>>([]);
  return (
    <div>
      <PageHead title="Bandeja editorial" description="Revisa y acepta contenido de tus fuentes."
        actions={<Btn variant="ghost" icon="activity" onClick={() => navigate('/editorial-sync')}>Estado de sincronización</Btn>} />
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ width: 300 }}><Input icon="search" placeholder="Buscar" onChange={() => {}} value="" /></div>
          <Select value="Pendiente" onChange={() => {}} options={['Pendiente', 'Aceptado', 'Rechazado']} />
        </div>
      </Panel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.length === 0 && <Panel><EmptyState icon="message" title="Bandeja vacía" body="No hay elementos pendientes de revisión." /></Panel>}
        {items.map(it => (
          <Panel key={it.id} pad={16}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ width: 160, flexShrink: 0 }}><Thumb radius={12} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div onClick={() => navigate('/editorial-inbox/' + it.id)} style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, marginBottom: 6, cursor: 'pointer' }}>{it.title}</div>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--body-muted)', lineHeight: 1.45 }}>Contenido recibido desde una fuente conectada, listo para revisión editorial antes de ingresar a la biblioteca.</p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Pill tone="info" size="sm">{it.source}</Pill><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{ago(it.addedAt)}</span><Pill tone="neutral" size="sm">{fmtDur(it.durationMs)}</Pill></div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                <Btn size="sm" variant="ghost" icon="eye" onClick={() => navigate('/editorial-inbox/' + it.id)}>Revisar</Btn>
                <Btn size="sm" variant="solid" icon="check" onClick={() => { setItems(x => x.filter(y => y.id !== it.id)); toast({ variant: 'success', message: 'Aceptado y añadido a la biblioteca.' }); }}>Aceptar</Btn>
                <Btn size="sm" variant="text" onClick={() => { setItems(x => x.filter(y => y.id !== it.id)); toast({ variant: 'info', message: 'Elemento rechazado.' }); }}>Rechazar</Btn>
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

/**
 * T328 (FR-005) — Real surface for `/feed-sources`.
 *
 * READ: `api.editorial.feeds.listAll` returns the projected rows of the
 * Payload-owned `ingest-sources` collection (synced through
 * `convex/sync/ingestSourcesReceiver.ts`). Honest-state primitives drive
 * the loading / empty branches — no fabricated mock rows.
 *
 * CREATE: chose approach (a) per task description — link-out to the
 * Payload admin create form. Rationale: ingestSources is one-way-synced;
 * the only legitimate write surface is Payload itself. Wrapping that
 * behind a Convex action proxy would (i) duplicate Payload's own
 * field-level validation, (ii) bypass Payload's admin audit log, and
 * (iii) re-introduce the dual-writer hazard the sync contract explicitly
 * forbids. The link-out keeps the integrity story simple and matches the
 * PRD §FR-005 acceptance criterion "a source created through the chosen
 * path carries a payloadId" (created in Payload → projected back through
 * the receiver → row arrives with its payloadId already populated).
 *
 * EDIT/VIEW per-row: same link-out into Payload's admin row page.
 *
 * VALIDATE per-row: calls `api.ingestSources.validate` (Convex action,
 * not a mutation) which probes the projected `sourceUrl`. The action
 * itself rejects non-https URLs with `E_INVALID_FEED_URL` — so even an
 * out-of-band Payload edit that violates the protocol guard surfaces as
 * a real failure, not a fake success toast.
 */
interface IngestSourceRow {
  _id?: string;
  payloadId?: string;
  name?: string;
  channelId?: string;
  protocol?: string;
  sourceUrl?: string;
  updatedAt?: number;
}

export function FeedSources() {
  const { toast } = useToast();
  const sources = useQuery(api.editorial.feeds.listAll, {}) as
    | IngestSourceRow[]
    | undefined;
  const validate = useAction(api.ingestSources.validate);
  const [validating, setValidating] = useState<string | null>(null);

  async function onValidate(row: IngestSourceRow): Promise<void> {
    if (!row.payloadId) {
      toast({ variant: 'error', message: 'No se puede validar: falta el identificador de Payload.' });
      return;
    }
    setValidating(row.payloadId);
    try {
      const result = await validate({ payloadId: row.payloadId });
      if (result.ok) {
        toast({
          variant: 'success',
          message: `Fuente accesible (HTTP ${result.statusCode}).`,
        });
      } else if (result.reason === 'E_INVALID_FEED_URL') {
        toast({
          variant: 'error',
          message: 'La URL de la fuente no es válida: debe usar https://.',
        });
      } else if (result.reason === 'http_error') {
        toast({
          variant: 'error',
          message: `La fuente respondió HTTP ${result.statusCode}. Revisa la URL o las credenciales.`,
        });
      } else if (result.reason === 'network_error') {
        toast({
          variant: 'error',
          message: 'No se pudo contactar la fuente. Verifica conectividad y vuelve a intentarlo.',
        });
      } else {
        toast({
          variant: 'error',
          message: `Validación falló (${result.reason}).`,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      toast({ variant: 'error', message: `Validación falló: ${message}` });
    } finally {
      setValidating(null);
    }
  }

  // Loading state: useQuery returns undefined until the first round-trip.
  if (sources === undefined) {
    return (
      <div>
        <PageHead title="Fuentes de contenido" description="Conecta feeds de socios — RSS, Atom o webhooks firmados — que Circa trae a tu bandeja para revisión."
          actions={<AccessGate perm="asset_create"><Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn></AccessGate>} />
        <SkeletonList rows={3} />
      </div>
    );
  }

  // Honest-empty state: no fabricated rows.
  if (sources.length === 0) {
    return (
      <div>
        <PageHead title="Fuentes de contenido" description="Conecta feeds de socios — RSS, Atom o webhooks firmados — que Circa trae a tu bandeja para revisión."
          actions={<AccessGate perm="asset_create"><Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn></AccessGate>} />
        <Panel>
          <EmptyState
            icon="database"
            title="Sin fuentes conectadas"
            body="Aún no hay fuentes conectadas. Mientras tanto, sube vídeos directamente a tu biblioteca."
            action={<AccessGate perm="asset_create"><Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn></AccessGate>}
          />
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <PageHead title="Fuentes de contenido" description="Conecta feeds de socios — RSS, Atom o webhooks firmados — que Circa trae a tu bandeja para revisión."
        actions={<AccessGate perm="asset_create"><Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn></AccessGate>} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sources.map((s: IngestSourceRow) => {
          const key = s._id ?? s.payloadId ?? s.name ?? Math.random().toString(36).slice(2);
          const displayName = s.name ?? 'Sin nombre';
          const displayUrl = s.sourceUrl ?? '—';
          const displayType = s.protocol ?? '—';
          const isValidating = validating === s.payloadId;
          return (
            <Panel key={key} pad={20}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Icon name="database" size={22} color="var(--muted-slate)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{displayName}</span>
                    <Pill tone="neutral" size="sm">{displayType}</Pill>
                  </div>
                  <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', textTransform: 'none' }}>{displayUrl}</div>
                </div>
                <Dropdown
                  trigger={<Btn variant="ghost" icon="moreH" />}
                  items={[
                    { label: isValidating ? 'Validando…' : 'Validar ahora', icon: 'refresh', onClick: () => { void onValidate(s); } },
                  ]}
                />
              </div>
            </Panel>
          );
        })}
      </div>
    </div>
  );
}

/**
 * NMW-398 / NMW-1168 — `/search` is a real, server-backed search over TWO
 * independent full-text indexes, in one box.
 *
 *   1. TITLES — `api.assetQueries.searchAssetsByTitle` scans the WHOLE
 *      `assets` table via the `search_title` index, so it finds matches
 *      regardless of age (the Library browse cap only ever loaded the newest
 *      100).
 *   2. VISUAL DESCRIPTION (captions) — `api.analysis.queries.
 *      searchEnrichmentContent` (NMW-1167, convex/analysis/queries.ts:328)
 *      scans the denormalised `enrichmentResults.searchText` column via the
 *      `search_content` index and returns the matching SEGMENTS, joined to
 *      their `sceneSegments` timing — so the answer is "this video, at 04:12".
 *
 * Scope note (corrected by MA-83, CONTRACTS.md sections 9.7 and 16.2; it
 * previously said dialogue search was not built): the media-analysis v2
 * pipeline transcribes speech and reads on-screen text, and `persist` writes
 * both into `enrichmentResults.searchText` beside the caption, so for an
 * asset with a CURRENT v2 run (section 9.5) the search covers DIALOGUE,
 * ON-SCREEN TEXT and VISUAL DESCRIPTION. An asset whose latest analysis is v1
 * (the retired stage-2, NMW-1162: `transcript: null` for every segment) is
 * still searchable by caption only, exactly as before (D-38). The amended
 * query answers, on every returned hit, `matchKind` (`dialogue`, `on_screen`,
 * `visual`, or `scene` when the term was not located inside the scene) and
 * `matchTimeMs` (the earliest cue or frame that matched, else the scene's
 * start); a v2 hit therefore deep links to `/library/<id>?t=<whole seconds>`
 * (time rule 5), which the asset page seeks to. Still NOT built: scene search
 * by visual similarity.
 *
 * The honesty that actually matters on this screen: content search can only
 * see assets whose analysis completed. Much of the fleet is unanalysed, so a
 * miss never means "that is not in this video"; it means "that video has not
 * been analysed", and a v1-analysed video was never searched by dialogue or
 * on-screen text. <ContentMatches> states that with the real fleet numbers
 * from `api.analysis.queries.getFleetCoverage` whenever they resolve, with a
 * qualitative sentence (flagged as such) when they do not, and per group with
 * the modalities its hits actually came from.
 *
 * RANKING DECISION (deliberate — NMW-1168): the two result sets are NOT merged
 * into one ranked list. Convex returns each index's own relevance order, and
 * scores from two different search indexes over two different corpora are not
 * comparable — interleaving them would invent a joint ranking that no backend
 * ever computed. So results are GROUPED: titles first (a user typing a title
 * expects that title at the top), then a separate, visibly-labelled dialogue
 * section, each preserving its own backend order. Within the dialogue section
 * segments are grouped under their asset, in first-hit order, because one
 * transcript commonly produces several hits on the same video.
 *
 * Honest states, per section, independently: `undefined` renders a skeleton,
 * `[]` renders that section's own empty state, a stub-throw surfaces as the
 * honest error toast. The dialogue section carries its OWN error boundary, so
 * a content-search failure degrades that section only and never takes the
 * (working) title search down with it.
 */
export function SmartSearch() {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const searching = debouncedQ.length > 0;
  const searchPending = q.trim().length > 0 && q.trim() !== debouncedQ;

  const raw = useQuery(
    api.assetQueries.searchAssetsByTitle,
    searching ? { searchTerm: debouncedQ, limit: 50 } : 'skip',
  ) as AssetListRow[] | undefined | null;

  // Honest error surface: a backend/stub throw makes `useQuery` throw on read,
  // which the error boundary catches. A null (defensive) → error toast, never a
  // fabricated result.
  useEffect(() => {
    if (raw === null) toast({ variant: 'error', message: 'No se pudo completar la búsqueda.' });
  }, [raw, toast]);

  const rows = useMemo(
    () => (raw == null ? undefined : raw.map(adaptAssetRow)),
    [raw],
  );

  return (
    <div>
      <PageHead title="Búsqueda inteligente" description="Encuentra tus videos por título o por su contenido analizado." />
      <Panel pad={16} style={{ marginBottom: 20 }}>
        <div style={{ maxWidth: 480 }}>
          <Input value={q} onChange={setQ} icon="search" placeholder="Buscar por título o por contenido analizado" />
        </div>
      </Panel>

      {!searching && !searchPending ? (
        <Panel>
          <EmptyState
            icon="search"
            title="Busca en toda tu biblioteca"
            body="Escribe un título para encontrar cualquier video, sin importar su antigüedad, o una frase de su contenido analizado para encontrar el momento exacto dentro del video."
            action={<Btn variant="ghost" icon="film" onClick={() => navigate('/library')}>Ir a la biblioteca</Btn>}
          />
        </Panel>
      ) : (
      <>
      {/* SECTION 1 — titles. Kept exactly as NMW-398 shipped it, minus the
          "Sin resultados" copy, which used to speak for the WHOLE page and now
          honestly speaks only for the title index. */}
      <SearchSectionHead
        label="Coincidencias por título"
        hint="Índice `search_title` — cubre todos los videos."
      />
      {rows === undefined || searchPending ? (
        <SkeletonCardGrid n={8} cols="circa-grid-4" media={120} />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon="search"
            title="Ningún título coincide"
            body={`Ningún título de video coincide con "${debouncedQ}". Revisa también las coincidencias en el contenido, más abajo.`}
          />
        </Panel>
      ) : (
        <div className="circa-grid-4">
          {rows.map((a) => (
            <div key={a.id} data-testid="title-match" onClick={() => navigate('/library/' + a.id)} className="circa-lift" style={{ cursor: 'pointer', border: '1px solid var(--border-cool)', borderRadius: 18, overflow: 'hidden', background: 'var(--canvas)' }}>
              <div style={{ position: 'relative' }}>
                <Thumb radius={0} label={a.type} />
                <span style={{ position: 'absolute', top: 10, left: 10 }}><Pill tone="neutral" size="sm" style={{ background: 'rgba(255,255,255,0.92)' }}>{a.type}</Pill></span>
                <span style={{ position: 'absolute', top: 10, right: 10 }}><Pill tone={assetStatusTone(a.status)} size="sm">{statusLabels[a.status]}</Pill></span>
                <span className="t-mono" style={{ position: 'absolute', bottom: 10, right: 10, fontSize: 10, background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 6px', borderRadius: 4 }}>{fmtDur(a.durationMs)}</span>
              </div>
              <div style={{ padding: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3, marginBottom: 8, height: 36, overflow: 'hidden' }}>{a.title}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>{a.resolution}</span>
                  {/* Match-kind marker: this card matched the TITLE index. Its
                      dialogue counterpart carries "Diálogo" / "Descripción
                      visual" — the two are never rendered the same way. */}
                  <Pill tone="neutral" size="sm">Título</Pill>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SECTION 2 — dialogue / visual description. Its own boundary: a
          content-search throw degrades THIS section only. */}
      <ContentMatches term={debouncedQ} pending={searchPending} />
      </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * NMW-1168 — content-search section
 * ------------------------------------------------------------------ */

/**
 * The tenant the enrichment rows are actually stamped with.
 *
 * `searchEnrichmentContent` takes `tenantId` as a REQUIRED, fail-closed arg —
 * a blank one throws rather than meaning "every tenant". The console is
 * single-publisher, and the stage-2 dispatcher pins the constant `"default"`
 * (convex/analysis/dispatch.ts:83), which is what rides the Heavy-Plane
 * callback back into `persistEnrichmentResults`. So `"default"` is the tenant
 * whose rows exist.
 *
 * Deliberately NOT `useTenantId()` (hooks/useCurrentMember.ts:126): that
 * returns the authenticated principal's subject, which no enrichment row
 * carries — passing it would make every dialogue search return `[]` forever,
 * i.e. a permanently fabricated "no matches". When real multi-tenancy lands
 * and the dispatcher stamps a real tenant, this constant flips to the hook and
 * nothing else here changes.
 */
const ENRICHMENT_TENANT_ID = 'default';

/** How many segment hits are listed per asset before "y N más". */
const SNIPPETS_PER_ASSET = 3;
/** Characters of context kept around the matching phrase. */
const SNIPPET_RADIUS = 90;

interface ContentMatchRow {
  _id: string;
  segmentId: string;
  assetId: string;
  segmentIndex?: number;
  startTimeMs?: number;
  endTimeMs?: number;
  durationMs?: number;
  transcript?: string;
  caption?: string;
  /** MA-83 (section 9.7): the frame's on-screen text; absent on v1 rows. */
  onScreenText?: string;
  /**
   * MA-83 (section 9.7): where in the scene the term matched — the earliest
   * matching cue or frame, else the scene's start. Absent only on the
   * pre-amendment answer shape, which is treated as a v1 hit.
   */
  matchTimeMs?: number;
  matchKind?: ContentMatchKind;
  asset: {
    _id: string;
    title?: string;
    assetType?: string;
    durationMs?: number;
    thumbnailUrl?: string;
  };
}

/** `searchEnrichmentContent`'s `matchKind` (CONTRACTS.md section 9.7). */
export type ContentMatchKind = 'dialogue' | 'on_screen' | 'visual' | 'scene';

/** The three kinds the console labels (section 16.2); `scene` is labelled from its text. */
const CONTENT_MATCH_KIND_LABELS: Record<Exclude<ContentMatchKind, 'scene'>, string> = {
  dialogue: 'Diálogo',
  on_screen: 'Texto en pantalla',
  visual: 'Descripción visual',
};

/**
 * MA-83 — the link of one hit (section 16.2, time rule 5): a hit that carries
 * a time links to the asset page at `?t=<floor(matchTimeMs / 1000)>`, which
 * `ContentDetail` reads with `parseDeepLinkSecond` and seeks once the player
 * has loaded; a hit without one (a v1 hit) keeps the plain asset link. A
 * non-finite or negative time is not a time, so it gets the plain link too —
 * never a fabricated `?t=`.
 */
export function contentHitHref(assetId: string, matchTimeMs: number | undefined): string {
  const base = '/library/' + assetId;
  if (typeof matchTimeMs !== 'number' || !Number.isFinite(matchTimeMs) || matchTimeMs < 0) return base;
  return base + '?t=' + Math.floor(matchTimeMs / 1000);
}

/**
 * MA-83 — whether a hit comes from a v2 analysis: the amended query answers
 * `matchTimeMs` on every returned hit, so its absence is the pre-amendment
 * (v1) shape. A `scene` kind with a time is a v2 hit the term was not located
 * in, or a hit past the per-query treatment limit (section 9.7).
 */
function isV2Hit(row: Pick<ContentMatchRow, 'matchTimeMs'>): boolean {
  return typeof row.matchTimeMs === 'number' && Number.isFinite(row.matchTimeMs);
}

interface FleetCoverageShape {
  window: number;
  scannedAssets: number;
  truncated: boolean;
  counts: Record<string, number>;
  /**
   * NMW-1214 — assets `search_content` can actually return. Always <=
   * `counts.enriched`; `undefined` when the backend does not report it, which
   * is stated as unknown rather than assumed equal.
   */
  searchableAssets?: number;
  enrichedNotSearchable?: number;
  searchProbeTruncatedAssets?: number;
}

/** Diacritic- and case-insensitive fold, so "television" finds "televisión". */
function foldForMatch(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/**
 * A window of `text` around the first occurrence of `term`.
 *
 * The backend index stems, so the literal term may legitimately be absent from
 * a genuine match. In that case we return the head of the text rather than
 * pretending we located the phrase — never a fabricated highlight.
 */
export function contentSnippet(text: string, term: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  const at = term.trim().length > 0
    ? foldForMatch(clean).indexOf(foldForMatch(term.trim()))
    : -1;
  if (at < 0) {
    return clean.length > SNIPPET_RADIUS * 2
      ? clean.slice(0, SNIPPET_RADIUS * 2).trimEnd() + '…'
      : clean;
  }
  const from = Math.max(0, at - SNIPPET_RADIUS);
  const to = Math.min(clean.length, at + term.trim().length + SNIPPET_RADIUS);
  return (from > 0 ? '…' : '') + clean.slice(from, to).trim() + (to < clean.length ? '…' : '');
}

/**
 * Which field this hit is shown from, and therefore how it is labelled.
 *
 * Prefer the field that literally contains the searched phrase. When neither
 * does (stemmed match), fall back to the transcript and label it neutrally —
 * claiming "diálogo" for a caption-only hit would be a small lie.
 */
export function matchKindOf(
  row: Pick<ContentMatchRow, 'transcript' | 'caption' | 'onScreenText' | 'matchKind'>,
  term: string,
): { label: string; text: string } | null {
  const t = (row.transcript ?? '').trim();
  const c = (row.caption ?? '').trim();
  const o = (row.onScreenText ?? '').trim();
  // MA-83: the backend located the term in a cue, an on-screen text or a
  // description (section 9.7) — its kind wins, shown from that modality's own
  // text. A kind whose text is missing falls through to the text rule below
  // rather than labelling an empty field.
  if (row.matchKind === 'dialogue' && t) return { label: CONTENT_MATCH_KIND_LABELS.dialogue, text: t };
  if (row.matchKind === 'on_screen' && o) return { label: CONTENT_MATCH_KIND_LABELS.on_screen, text: o };
  if (row.matchKind === 'visual' && c) return { label: CONTENT_MATCH_KIND_LABELS.visual, text: c };
  const needle = foldForMatch(term.trim());
  if (needle.length > 0 && t && foldForMatch(t).includes(needle)) {
    return { label: 'Diálogo', text: t };
  }
  if (needle.length > 0 && o && foldForMatch(o).includes(needle)) {
    return { label: 'Texto en pantalla', text: o };
  }
  if (needle.length > 0 && c && foldForMatch(c).includes(needle)) {
    return { label: 'Descripción visual', text: c };
  }
  if (t) return { label: 'Contenido analizado', text: t };
  if (o) return { label: 'Contenido analizado', text: o };
  if (c) return { label: 'Contenido analizado', text: c };
  return null;
}

/**
 * MA-83 — the per-group modality note (section 16.2, "coverage notes per
 * modality"). A v2 group names the modalities its hits came from; a group with
 * no v2 hit says plainly that dialogue and on-screen text were not searched in
 * that video, so a caption-only answer is never read as "nothing was said".
 */
export function groupModalityNote(hits: Array<Pick<ContentMatchRow, 'matchTimeMs' | 'matchKind'>>): {
  analysis: 'v1' | 'v2';
  text: string;
} {
  if (!hits.some(isV2Hit)) {
    return {
      analysis: 'v1',
      text: 'Análisis anterior: solo se buscó la descripción visual. El diálogo y el texto en pantalla no se buscaron en este video.',
    };
  }
  const found = new Set<ContentMatchKind>();
  for (const hit of hits) if (hit.matchKind && hit.matchKind !== 'scene') found.add(hit.matchKind);
  const words = (['dialogue', 'on_screen', 'visual'] as const)
    .filter((k) => found.has(k))
    .map((k) => CONTENT_MATCH_KIND_LABELS[k].toLowerCase());
  const where = words.length === 0
    ? 'la frase coincide con la escena, sin ubicarse en una frase de diálogo, un texto en pantalla ni una descripción'
    : `coincidencias en ${words.join(', ')}`;
  return {
    analysis: 'v2',
    text: `Análisis completo: se buscó diálogo, texto en pantalla y descripción visual; ${where}.`,
  };
}

/** Milliseconds → the `hh:mm:ss` timecode the transcript tab also uses; floored, so it agrees with the deep link second (time rule 5). */
function msToClock(ms?: number): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) return null;
  return fmtClock(Math.floor(ms / 1000) * 1000);
}

function SearchSectionHead({ label, hint }: { label: string; hint: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '4px 0 12px' }}>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 500 }}>{label}</div>
      <div className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>{hint}</div>
    </div>
  );
}

/**
 * Coverage honesty line.
 *
 * With the real fleet counts (`getFleetCoverage`, NMW-1169) it states how many
 * of the scanned assets this search can actually reach. Without them it
 * degrades to a qualitative sentence that SAYS it is qualitative — it never
 * quietly implies full coverage, and it never reports a number it does not
 * have.
 *
 * NMW-1214 — the number stated is `searchableAssets` (rows with indexed text),
 * NOT `counts.enriched`. An enriched asset whose enrichment carries no
 * `searchText` is invisible to THIS search, so counting it here would state a
 * reach the query does not have. When the two diverge, both are shown.
 */
function CoverageNote({ coverage }: { coverage: FleetCoverageShape | undefined | null }) {
  let body: string;
  if (coverage === undefined) {
    body = 'La búsqueda por contenido solo alcanza los videos ya analizados; una parte de la biblioteca aún no lo está. (Cobertura exacta: calculando…)';
  } else if (coverage === null) {
    body = 'La búsqueda por contenido solo alcanza los videos ya analizados; una parte de la biblioteca aún no lo está. (Estimación cualitativa: no se pudo leer la cobertura real.)';
  } else {
    const enriched = coverage.counts?.enriched ?? 0;
    const searchable = coverage.searchableAssets;
    const scope = coverage.truncated
      ? `los ${coverage.scannedAssets} videos más recientes`
      : `${coverage.scannedAssets} videos`;
    if (typeof searchable !== 'number') {
      body = `La búsqueda por contenido solo alcanza los videos con texto indexado. De ${scope}, ${enriched} están analizados; cuántos son realmente buscables no se informó. Un video sin analizar no aparece aquí aunque contenga la frase.`;
    } else if (searchable < enriched) {
      const gap = coverage.enrichedNotSearchable ?? enriched - searchable;
      body = `La búsqueda por contenido solo alcanza ${searchable} de ${scope}. Hay ${enriched} analizados, pero ${gap} no dejaron texto indexado y no se pueden encontrar por diálogo. Un video sin analizar tampoco aparece aquí aunque contenga la frase.`;
    } else {
      body = `La búsqueda por contenido solo alcanza los videos ya analizados: ${searchable} de ${scope}. Un video sin analizar no aparece aquí aunque contenga la frase.`;
    }
  }
  // MA-83 — the modality note: which modalities the complete (v2) analysis
  // makes searchable, and that a v1 analysis never covered two of them.
  const modalities = 'Diálogo y texto en pantalla solo se encuentran en los videos con el análisis completo; un análisis anterior solo dejó la descripción visual.';
  return (
    <div
      data-testid="content-coverage-note"
      style={{ fontSize: 12, color: 'var(--body-muted)', background: 'var(--lightest-gray)', borderRadius: 8, padding: '8px 10px', marginBottom: 14 }}
    >
      {body} {modalities}
    </div>
  );
}

function ContentSearchError() {
  return (
    <Panel>
      <EmptyState
        icon="alertTriangle"
        title="No se pudo buscar en el contenido"
        body="La búsqueda por contenido falló. Las coincidencias por título siguen siendo válidas; las de contenido no se pudieron calcular — no se muestran como «sin resultados»."
      />
    </Panel>
  );
}

/**
 * Per-section error boundary. `useQuery` throws on read when the backend
 * throws, and without this the whole /search screen (title results included)
 * would be replaced by the route-level error surface.
 */
class ContentMatchesBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { failed: boolean }
> {
  override state = { failed: false };
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override componentDidCatch(error: unknown): void {
    // Keys, not values (V3I-INV-12) — the searched phrase is user text.
    console.error('[SmartSearch] content search failed', error);
    this.props.onError();
  }
  override render(): React.ReactNode {
    if (this.state.failed) return <ContentSearchError />;
    return this.props.children;
  }
}

export function ContentMatches({ term, pending }: { term: string; pending: boolean }) {
  const { toast } = useToast();
  const notified = useRef<string | null>(null);
  const onError = useCallback(() => {
    if (notified.current === term) return;
    notified.current = term;
    toast({ variant: 'error', message: 'No se pudo buscar en el contenido de los videos.' });
  }, [term, toast]);

  return (
    <div style={{ marginTop: 28 }}>
      <SearchSectionHead
        label="Coincidencias en el contenido"
        hint="Índice `search_content` — solo videos analizados."
      />
      {/* Remount per term so a failure on one phrase does not stick to the next. */}
      <ContentMatchesBoundary key={term} onError={onError}>
        <ContentMatchesInner term={term} pending={pending} />
      </ContentMatchesBoundary>
    </div>
  );
}

function ContentMatchesInner({ term, pending }: { term: string; pending: boolean }) {
  const active = term.length > 0;

  const raw = useQuery(
    api.analysis.queries.searchEnrichmentContent,
    active ? ({ searchTerm: term, tenantId: ENRICHMENT_TENANT_ID, limit: 50 } as never) : 'skip',
  ) as ContentMatchRow[] | undefined | null;

  // Coverage is only fetched while a search is live — it walks a window of the
  // fleet, so it is not free.
  const coverage = useQuery(
    api.analysis.queries.getFleetCoverage,
    active ? ({} as never) : 'skip',
  ) as FleetCoverageShape | undefined | null;

  // Group segment hits under their asset, preserving the backend's own order
  // (see the RANKING DECISION note above — no re-scoring happens here).
  const groups = useMemo(() => {
    if (raw == null) return undefined;
    const byAsset = new Map<string, { asset: ContentMatchRow['asset']; hits: ContentMatchRow[] }>();
    for (const row of raw) {
      const key = String(row.assetId);
      const g = byAsset.get(key);
      if (g) g.hits.push(row);
      else byAsset.set(key, { asset: row.asset, hits: [row] });
    }
    return [...byAsset.entries()].map(([id, g]) => ({ id, ...g }));
  }, [raw]);

  // A term typed but not yet debounced: the query has not been issued, so the
  // truthful state is "loading", not "no matches".
  if (!active) return pending ? <SkeletonList rows={4} /> : null;
  if (pending || groups === undefined) return <SkeletonList rows={4} />;

  if (groups.length === 0) {
    return (
      <>
        <CoverageNote coverage={coverage} />
        <Panel>
          <EmptyState
            icon="search"
            title="Ningún contenido analizado coincide"
            body={`Ninguno de los videos ya analizados dice "${term}". Los videos sin analizar no se consultaron.`}
          />
        </Panel>
      </>
    );
  }

  return (
    <>
      <CoverageNote coverage={coverage} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {groups.map((g) => {
          const shown = g.hits.slice(0, SNIPPETS_PER_ASSET);
          const more = g.hits.length - shown.length;
          // The group badge must DESCRIBE the hits, never assert a kind. It
          // used to be a hardcoded "Diálogo", which since the 2026-08-20
          // transcription rescope would have mislabelled every single group:
          // stage-2 emits no transcripts, so every hit is caption-derived.
          const kinds = new Set(
            g.hits.map((h) => matchKindOf(h, term)?.label).filter(Boolean) as string[],
          );
          const groupLabel =
            kinds.size === 1 ? [...kinds][0] : 'Contenido analizado';
          const modalityNote = groupModalityNote(g.hits);
          return (
            <Panel key={g.id} pad={16}>
              <div
                data-testid="content-match"
                className="circa-lift"
                onClick={() => navigate('/library/' + g.id)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, flex: 1, minWidth: 0 }}>{g.asset?.title ?? '—'}</div>
                  <Pill tone="info" size="sm">{groupLabel}</Pill>
                </div>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {shown.map((hit) => {
                    const kind = matchKindOf(hit, term);
                    // MA-83: a v2 hit's time is where the term matched; a v1
                    // hit's is its scene's start (the only time it has).
                    const v2 = isV2Hit(hit);
                    const clock = msToClock(v2 ? hit.matchTimeMs : hit.startTimeMs);
                    const href = contentHitHref(g.id, v2 ? hit.matchTimeMs : undefined);
                    if (!kind) return null;
                    return (
                      <li
                        key={hit._id}
                        data-testid="content-hit"
                        data-kind={hit.matchKind ?? 'v1'}
                        data-href={href}
                        onClick={(e) => {
                          // One click, one navigation: the card's own link
                          // (the plain asset page) must not fire as well.
                          e.stopPropagation();
                          navigate(href);
                        }}
                        style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}
                      >
                        <span
                          className="t-mono"
                          data-testid="content-match-timecode"
                          style={{ fontSize: 11, color: 'var(--muted-slate)', flexShrink: 0, paddingTop: 2 }}
                        >
                          {clock ?? '—:—:—'}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.45 }}>
                          <Pill tone="neutral" size="sm" style={{ marginRight: 8 }}>{kind.label}</Pill>
                          {contentSnippet(kind.text, term)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {more > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--muted-slate)', marginTop: 8 }}>
                    y {more} {more === 1 ? 'coincidencia más' : 'coincidencias más'} en este video.
                  </div>
                )}
                <div
                  data-testid="content-match-modalities"
                  data-analysis={modalityNote.analysis}
                  style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 8 }}
                >
                  {modalityNote.text}
                </div>
              </div>
            </Panel>
          );
        })}
      </div>
    </>
  );
}

export function AiSuggestions() {
  // T318 — wired to api.assetQueries.listAllAssets. The analysis-result
  // counts ("3 cambios de escena", "2 silencios", "2 alta confianza") are
  // NOT exposed by the public assets query, so they are honestly omitted
  // until a public scene/analysis index lands. Until then the list shows
  // recently-added videos with a "Revisar" affordance into the marker
  // editor — never fabricated analysis numbers.
  const aiAssets = useQuery(api.assetQueries.listAllAssets, {}) as
    | Array<{ _id: string; title?: string }>
    | undefined;
  return (
    <div>
      <PageHead title="Sugerencias inteligentes" description="Videos analizados recientemente." />
      {aiAssets === undefined ? (
        <SkeletonList rows={5} />
      ) : aiAssets.length === 0 ? (
        <EmptyState
          icon="sparkles"
          title="Sin sugerencias"
          body="Aún no hay videos analizados en este espacio de trabajo."
        />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {aiAssets.slice(0, 5).map(a => (
          <Panel key={a._id} pad={16}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 130, flexShrink: 0 }}><Thumb radius={10} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 500, marginBottom: 6 }}>{a.title ?? '—'}</div>
              </div>
              <Btn size="sm" variant="ghost" iconRight="arrowRight" onClick={() => navigate('/library/' + a._id + '/markers')}>Revisar</Btn>
            </div>
          </Panel>
        ))}
      </div>
      )}
    </div>
  );
}

